<div class="w-full h-full flex flex-col bg-body-background text-primary-text font-sans relative">
  <!-- App Page Header -->
  <div
    class="w-full bg-body-background py-2.5 px-5 shrink-0 flex items-center justify-between gap-4 border-b border-secondary-border">
    <!-- Left Area: Back Button, Title & Version -->
    <div class="flex items-center gap-2.5 min-w-0">
      <button (click)="goBack()" title="Back"
        class="text-secondary-text hover:text-primary transition-colors focus:outline-none w-8 h-8 rounded border border-secondary-border hover:bg-hover-default flex items-center justify-center shadow-sm bg-component-inner shrink-0 cursor-pointer">
        <i class="fas fa-arrow-left text-xs"></i>
      </button>
      <div class="flex items-center gap-2.5 min-w-0">
        <h1 class="text-lg sm:text-xl font-bold !m-0 !mb-0 tracking-tight text-heading leading-none truncate"
          style="margin: 0 !important; margin-bottom: 0 !important;">
          {{ appName() }}
        </h1>
        @if (appVersion()) {
        <nz-tag class="!m-0 text-xs text-secondary-text font-mono font-medium shrink-0">
          {{ appVersion()?.startsWith('v') ? appVersion() : 'v' + appVersion() }}
        </nz-tag>
        }
      </div>
    </div>

    <!-- Right Area: Page Selector, Quick Actions, Report & Request, More Actions -->
    <div class="flex items-center gap-2.5 shrink-0">
      <!-- Page Selector Button (Matches Report & Request button design) -->
      <button nz-dropdown nzTrigger="click" [nzDropdownMenu]="pageMenu" nzPlacement="bottomRight"
        class="flex items-center justify-center h-8 px-2.5 gap-1.5 text-[13px] text-secondary-text bg-component-inner hover:bg-hover-default hover:text-primary rounded border border-secondary-border shadow-sm transition-colors focus:outline-none cursor-pointer"
        title="Switch Page">
        <i class="fa-regular fa-file-lines text-xs opacity-75"></i>
        <span class="font-medium text-xs whitespace-nowrap max-w-[180px] truncate">
          {{ getActivePageName() || activePageId() || 'Overview' }}
        </span>
        <i class="fas fa-chevron-down text-[10px] opacity-70 ml-0.5"></i>
      </button>
      <nz-dropdown-menu #pageMenu="nzDropdownMenu">
        <ul nz-menu class="min-w-[200px]">
          @for (page of installedManifestPages(); track page.route) {
          @if (page.displayInMenu !== false) {
          <li nz-menu-item (click)="navigateToPage(page.route)">
            {{ page.name || page.route }}
          </li>
          }
          }
        </ul>
      </nz-dropdown-menu>

      <!-- Quick Actions -->
      @if (aiActions().length > 0) {
      <nz-select class="w-64" nzShowSearch nzPlaceHolder="AI Quick Actions" (ngModelChange)="runAiAction($event)"
        [ngModel]="null">
        @for (action of aiActions(); track action.key) {
        <nz-option [nzLabel]="action.value?.title" [nzValue]="action"></nz-option>
        }
      </nz-select>
      }

      <!-- Tasks Side Menu (Lamp Icon & Label) -->
      <button (click)="openTasksSidebar()"
        class="flex items-center justify-center h-8 w-8 sm:w-auto px-2 sm:px-2.5 gap-1.5 text-[13px] rounded border shadow-sm transition-colors focus:outline-none relative cursor-pointer"
        [ngClass]="isSidebarOpen() && activeSidebarTab() === 'Tasks'
          ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white border-gray-400 font-semibold shadow-inner'
          : 'text-secondary-text bg-component-inner hover:bg-hover-default border-secondary-border'"
        title="Report & Request" nz-tooltip nzTooltipTitle="Report & Request (Tasks, Bugs & Features)">
        <i class="fa-solid fa-lightbulb text-xs"
          [ngClass]="isSidebarOpen() && activeSidebarTab() === 'Tasks' ? 'text-amber-500' : ''"></i>
        <span class="hidden sm:inline font-medium text-xs whitespace-nowrap"
          i18n="@@dynamicAppViewer.reportAndRequest">Report & Request</span>
      </button>

      <!-- More Actions (Ellipsis Menu) -->
      <button nz-dropdown nzTrigger="click" [nzDropdownMenu]="moreMenu" nzPlacement="bottomRight"
        [class.bg-primary]="isSidebarOpen() && activeSidebarTab() === 'E2E Tests'"
        [class.text-white]="isSidebarOpen() && activeSidebarTab() === 'E2E Tests'"
        [class.border-primary]="isSidebarOpen() && activeSidebarTab() === 'E2E Tests'"
        class="flex items-center justify-center text-secondary-text bg-component-inner hover:bg-hover-default px-2 py-1 text-[13px] h-8 w-8 rounded border border-secondary-border shadow-sm transition-colors focus:outline-none cursor-pointer"
        title="More Actions">
        <i class="fas fa-ellipsis-vertical"></i>
      </button>
      <nz-dropdown-menu #moreMenu="nzDropdownMenu">
        <ul nz-menu>
          <li nz-menu-item (click)="openE2ESidebar()">
            <i class="fa-solid fa-flask mr-2 text-primary"></i> E2E Tests
          </li>
          <li nz-menu-item (click)="toggleArtifactsSidebar()">
            <i class="fas fa-box-open mr-2 text-secondary-text"></i> Artifacts
          </li>
        </ul>
      </nz-dropdown-menu>
    </div>
  </div>

  <div class="flex-1 w-full flex flex-col min-h-0 min-w-0 relative">
    <!-- Main Content Area -->
    <ng-template #mainContentTemplate>
      <div class="flex-1 min-w-0 h-full flex flex-col relative" #canvasWrapper>
        @if (!activePageId() && !activePageUrl() && isLoading()) {
        <div class="flex items-center justify-center p-12 h-full w-full min-h-[400px]">
          <div class="flex flex-col items-center gap-4">
            <i class="fas fa-circle-notch fa-spin text-5xl text-primary"></i>
            <span class="text-secondary-text font-medium text-lg">{{
              configTexts.loadingApp
              }}</span>
          </div>
        </div>
        } @else if (error()) {
        <div class="flex items-center justify-center h-full w-full min-h-[400px]">
          <div
            class="p-8 text-center text-danger bg-danger/5 border border-danger/20 rounded-xl m-4 max-w-md shadow-sm flex flex-col items-center">
            <i class="fas fa-exclamation-triangle text-4xl mb-3"></i>
            <p class="font-medium text-danger/80 mb-4">{{ error() }}</p>
            <button nz-button nzType="primary" (click)="goBack()"
              class="bg-primary text-white font-semibold flex items-center gap-2 px-4 py-2 rounded-lg">
              <i class="fas fa-arrow-left mr-1"></i>
              <span>Back to App Builder</span>
            </button>
          </div>
        </div>
        } @else {
        <!-- Overlay Spinner during inner page transitions to prevent destroying the iframe -->
        @if (isLoading()) {
        <div
          class="absolute inset-0 z-30 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center pointer-events-none transition-opacity duration-200">
          <div
            class="flex flex-col items-center gap-3 bg-slate-950/80 px-5 py-4 rounded-xl border border-slate-700 shadow-2xl">
            <i class="fas fa-circle-notch fa-spin text-3xl text-primary"></i>
            <span class="text-slate-200 font-medium text-xs">Loading Page...</span>
          </div>
        </div>
        }
        <div class="relative w-full flex-1 flex flex-col overflow-hidden min-h-0" #captureZone
          (mousemove)="handleCaptureZoneMouseMove($event)" (mouseout)="handleCaptureZoneMouseOut($event)"
          (click)="handleCaptureZoneClick($event)" (wheel)="onCaptureZoneWheel($event)"
          (scroll)="onCaptureZoneScroll($event)" [class.cursor-crosshair]="isAddCommentMode()">
          @if (activePageType() === 'AppPage') {
          <div class="flex-1 min-h-0 w-full h-full relative flex flex-col">
            @if (
            activePageUrl() &&
            (activePageUrl()!.startsWith('http://') ||
            activePageUrl()!.startsWith('https://'))
            ) {
            <rapider-html-url-preview class="w-full h-full flex-1 min-h-0 flex flex-col" [htmlUrl]="activePageUrl()!"
              [appName]="appName()" [pagesManifest]="installedManifestPages()" [dataModels]="effectiveDataModels()"
              [routeParams]="currentRouteParams()" [testMode]="testMode()" [allowSameOrigin]="needsIframeDomAccess()"
              [assetBaseUrl]="activeAssetBaseUrl()" [appTheme]="activeAppTheme()" [displayBrowserHeader]="false">
            </rapider-html-url-preview>
            } @else if (activePageId()) {
            <rapider-html-definition-preview class="w-full h-full flex-1 min-h-0 flex flex-col"
              [definitionId]="activePageId()!" [displayBrowserHeader]="false" [routeParams]="currentRouteParams()"
              [fileName]="activePageFileName()" [testMode]="testMode()" [allowSameOrigin]="needsIframeDomAccess()"
              [assetBaseUrl]="activeAssetBaseUrl()" [appTheme]="activeAppTheme()">
            </rapider-html-definition-preview>
            }
          </div>
          } @else if (
          activePageType() === 'AppDynamicPage' && activePageConfig()
          ) {
          <div class="flex-1 min-h-0 w-full relative">
            <rapider-dynamic-page-renderer [pageConfig]="activePageConfig()!" [manifestPages]="installedManifestPages()"
              [initialPayload]="currentQueryParams()" [appId]="currentAppId() || ''"
              [appDefinition]="activeManifest() || appConfig()">
            </rapider-dynamic-page-renderer>
          </div>
          }
        </div>

        <!-- Add Comment / Drop Bubble Instruction Banner on Canvas -->
        @if (isAddCommentMode()) {
        <div
          class="absolute top-4 left-1/2 transform -translate-x-1/2 z-[60] bg-gray-900/95 text-white px-4 py-2 rounded-full shadow-2xl border border-gray-700 flex items-center gap-3">
          <div class="w-6 h-6 rounded-full bg-rose-500 text-white flex items-center justify-center text-xs">
            <i class="fa-solid fa-bug"></i>
          </div>
          <span class="text-xs font-medium">
            Click anywhere on the preview below to report a bug on this element
          </span>
          <button type="button" (click)="cancelAddCommentMode()"
            class="text-gray-400 hover:text-white text-xs bg-gray-800 hover:bg-gray-700 px-2 py-0.5 rounded-full transition cursor-pointer">
            Cancel
          </button>
        </div>
        }

        <!-- Rendered Bubbles over Preview Canvas using Recalculated Positions -->
        @if (showBubbles() && isSidebarOpen() && activeSidebarTab() === 'Tasks' && !isCanvasScrolling()) {
        @for (task of filteredTasks(); track task.rawId) {
        @if (isTaskOnActivePage(task) && bubblePositions()[task.rawId]) {
        <div (click)="onBubbleClick(task, $event)"
          class="absolute w-8 h-8 -mt-8 origin-bottom-left rounded-full rounded-bl-sm text-white flex items-center justify-center font-bold shadow-md cursor-pointer hover:scale-110 transition-transform z-40 comment-bubble text-xs border-2 border-white"
          [class.comment-bubble-active]="selectedTaskId() === task.rawId"
          [ngClass]="selectedTaskId() === task.rawId ? 'bg-amber-500' : (task.type === 'Bug' ? 'bg-[orangered]' : 'bg-blue-600')"
          [style.top.px]="bubblePositions()[task.rawId].top" [style.left.px]="bubblePositions()[task.rawId].left"
          [style.margin-top.px]="-32"
          [style.font-size]="(task.bubbleNumber || 0) > 99 ? 'x-small' : null" nz-tooltip [nzTooltipTitle]="task.title"
          nzTooltipPlacement="top">
          {{ formatBubbleNumber(task.bubbleNumber) }}
        </div>
        }
        }
        }
        }
      </div>
    </ng-template>

    <!-- Splitter (always keeps mainContentTemplate mounted so opening/closing sidebar never destroys the iframe) -->
    <nz-splitter #appSplitter class="w-full h-full flex-1 min-w-0 min-h-0" [nzLayout]="'horizontal'"
      (nzResize)="onSidebarSplitResize($event)" (nzResizeEnd)="onSidebarSplitResizeEnd($event)">
      <!-- Left Pane (Main Content: elastic auto-fill 100% of remaining space) -->
      <nz-splitter-panel [nzMin]="300">
        <ng-container *ngTemplateOutlet="mainContentTemplate"></ng-container>
      </nz-splitter-panel>

      @if (isSidebarOpen()) {
      <!-- Right Pane (Sidebar) -->
      <nz-splitter-panel [nzDefaultSize]="sidebarWidthPx()" [nzMin]="320" [nzMax]="800">
        <div
          class="w-full border-l border-gray-200 bg-white flex flex-col h-full shadow-lg relative z-10 overflow-hidden">
          <ng-container *ngTemplateOutlet="sidebarTabsTemplate"></ng-container>

          <!-- Sidebar Content -->
          <div class="flex-1 bg-gray-50/50 flex flex-col min-h-0"
            [ngClass]="activeSidebarTab() === 'Tasks' && selectedTaskForDetail() ? 'overflow-hidden p-0' : 'overflow-y-auto p-4'">
            @if (activeSidebarTab() === 'Tasks') {
            @if (selectedTaskForDetail(); as detailTask) {
            <!-- In-Panel Task Details View (Replaces Separate Drawer) -->
            <div class="flex-1 flex flex-col h-full min-h-0 overflow-hidden">
              <!-- FIXED / STICKY HEADER (DOES NOT SCROLL) -->
              <div class="bg-white border-b border-gray-200 px-4 py-3 shrink-0 shadow-xs space-y-2.5 z-10">
                <!-- Top Navigation Bar with Back Button -->
                <div class="flex items-center justify-between">
                  <button type="button" (click)="closeTaskDetailDrawer()"
                    class="text-xs font-semibold text-gray-700 hover:text-primary flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-gray-100 transition cursor-pointer"
                    title="Back to Tasks">
                    <i class="fa-solid fa-arrow-left text-xs"></i>
                    <span>Back to Tasks</span>
                  </button>

                  <div class="flex items-center gap-1.5">
                    @if (canEditTask(detailTask) && !isEditingTaskDetail()) {
                    <button type="button" (click)="startEditingTask(detailTask, $event)"
                      class="px-2.5 py-1 text-xs font-semibold text-gray-700 hover:text-primary bg-gray-50 hover:bg-primary/5 border border-gray-200 rounded-lg flex items-center gap-1.5 transition cursor-pointer"
                      title="Edit task title, description, or type">
                      <i class="fa-regular fa-pen-to-square text-[11px]"></i>
                      <span>Edit</span>
                    </button>
                    }
                    <button type="button" (click)="confirmDeleteTask(detailTask, $event)"
                      class="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition cursor-pointer"
                      title="Delete Task">
                      <i class="fa-regular fa-trash-can text-xs"></i>
                    </button>
                  </div>
                </div>

                <!-- Task Badges & ID & Status Dropdown/Badge -->
                <div class="flex items-center justify-between gap-2 flex-wrap">
                  <div class="flex items-center space-x-2">
                    <span
                      class="w-6 h-6 rounded-full text-white text-[11px] font-bold flex items-center justify-center shrink-0 shadow-xs"
                      [ngClass]="detailTask.type === 'Bug' ? 'bg-[orangered]' : 'bg-blue-600'"
                      [style.font-size]="(detailTask.bubbleNumber || 0) > 99 ? 'x-small' : null">
                      {{ formatBubbleNumber(detailTask.bubbleNumber) }}
                    </span>
                    <span class="text-xs font-mono font-bold text-gray-700">{{ detailTask.id }}</span>
                    @if (detailTask.type === 'Bug') {
                    <span
                      class="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                      <i class="fa-solid fa-circle-exclamation text-[10px] mr-1"></i> Bug
                    </span>
                    } @else {
                    <span
                      class="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                      <i class="fa-solid fa-sparkles text-[10px] mr-1"></i> Feature
                    </span>
                    }
                  </div>

                  <div class="flex items-center space-x-1.5">
                    <!-- Workflow Status: Dropdown for Admin & Owner, Static Badge for Others -->
                    @if (canApproveTasks()) {
                    <button type="button" nz-dropdown [nzDropdownMenu]="statusDropdownMenu" [nzTrigger]="'click'"
                      class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border transition cursor-pointer shadow-2xs"
                      [ngClass]="getTaskStatusClass(detailTask.status)" title="Change Status (Admin / Owner)">
                      <span class="text-[10px] opacity-70 font-normal uppercase">Status:</span>
                      <span>{{ getDisplayStatus(detailTask.status) }}</span>
                      <i class="fa-solid fa-chevron-down text-[8px] opacity-70 ml-0.5"></i>
                    </button>
                    <nz-dropdown-menu #statusDropdownMenu="nzDropdownMenu">
                      <ul nz-menu class="min-w-44 text-xs shadow-lg rounded-lg border border-gray-100 py-1 bg-white">
                        @for (st of taskStatusOptions; track st) {
                        <li nz-menu-item
                          [nzDisabled]="(st === 'Confirmed' || st === 'Reopened' || st === 'Resolved' || st === 'Reporter Rejected') && detailTask.status !== 'Released'"
                          (click)="((st !== 'Confirmed' && st !== 'Reopened' && st !== 'Resolved' && st !== 'Reporter Rejected') || detailTask.status === 'Released') && setTaskStatus(detailTask, st, $event)"
                          [nzSelected]="getNormalizedTaskStatus(detailTask) === st"
                          [title]="(st === 'Confirmed' || st === 'Reopened') && detailTask.status !== 'Released' ? 'Reporter verification can only be performed after a version is released' : ''"
                          class="flex items-center justify-between text-xs px-3 py-1.5 hover:bg-gray-50 cursor-pointer">
                          <span class="font-medium">{{ st }}</span>
                          @if (getNormalizedTaskStatus(detailTask) === st) {
                          <i class="fa-solid fa-check text-xs text-primary"></i>
                          }
                        </li>
                        }
                      </ul>
                    </nz-dropdown-menu>
                    } @else {
                    <span class="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border"
                      [ngClass]="getTaskStatusClass(detailTask.status)">
                      <span class="text-[10px] opacity-70 font-normal uppercase mr-1">Status:</span>
                      {{ getDisplayStatus(detailTask.status) }}
                    </span>
                    }
                  </div>
                </div>
              </div>

              <!-- SCROLLABLE BODY (Only this content scrolls) -->
              <div class="flex-1 overflow-y-auto p-4 space-y-4">
                <!-- Owner / Admin Approval Control Box -->
                @if (canApproveTasks()) {
                <div class="p-3.5 rounded-xl border flex flex-col gap-2.5 transition-colors" [ngClass]="{
                            'bg-emerald-50/60 border-emerald-200': detailTask.isApproved === true,
                            'bg-rose-50/60 border-rose-200': detailTask.isApproved === false,
                            'bg-amber-50/60 border-amber-200': detailTask.isApproved == null
                          }">
                  <div class="flex items-center justify-between gap-2">
                    <div class="flex items-center gap-2 min-w-0">
                      <i class="fa-solid text-base shrink-0" [ngClass]="{
                                  'fa-circle-check text-emerald-600': detailTask.isApproved === true,
                                  'fa-circle-xmark text-rose-600': detailTask.isApproved === false,
                                  'fa-shield-halved text-amber-500': detailTask.isApproved == null
                                }"></i>
                      <div class="min-w-0">
                        <span class="text-xs font-bold block truncate" [ngClass]="{
                                    'text-emerald-900': detailTask.isApproved === true,
                                    'text-rose-900': detailTask.isApproved === false,
                                    'text-amber-900': detailTask.isApproved == null
                                  }">
                          @if (detailTask.isApproved === true) {
                          Approved for AI Execution
                          } @else if (getNormalizedTaskStatus(detailTask) === 'Closed' || detailTask.isApproved ===
                          false) {
                          Closed by Owner / Admin
                          } @else {
                          Triage & Approval Required
                          }
                        </span>
                        <span class="text-[11px] text-gray-500 block truncate">
                          @if (detailTask.isApproved === true) {
                          @if (getNormalizedTaskStatus(detailTask) !== 'Open' && getNormalizedTaskStatus(detailTask) !==
                          'Accepted') {
                          Approval locked while task is in progress or completed.
                          } @else {
                          Autonomous worker will ingest and implement this task.
                          }
                          } @else if (getNormalizedTaskStatus(detailTask) === 'Closed' || detailTask.isApproved ===
                          false) {
                          This task is closed and will not be automated.
                          } @else {
                          Approve to queue for AI worker, or close in 1 click (message optional).
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Action Buttons: Open -> Approve + Close (optional note); Closed -> Reopen only -->
                  @if (canModifyApproval(detailTask)) {
                  @if (getNormalizedTaskStatus(detailTask) === 'Closed' || detailTask.isApproved === false) {
                  <div class="flex items-center gap-2 pt-1 border-t border-rose-200/60">
                    <button type="button" (click)="setTaskApproval(detailTask, null, $event)"
                      class="w-full px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-xs bg-white hover:bg-gray-50 text-gray-800 border border-gray-300"
                      title="Reopen this task and move back to Open">
                      <i class="fa-solid fa-rotate-left"></i>
                      <span>Reopen</span>
                    </button>
                  </div>
                  } @else {
                  <div class="pt-1.5 border-t border-gray-200/60 space-y-2">
                    <input type="text" [ngModel]="triageNote()" (ngModelChange)="triageNote.set($event)"
                      placeholder="Optional note (leave blank to close or approve immediately)..."
                      class="w-full text-[11px] px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary" />
                    <div class="flex items-center gap-2">
                      @if (detailTask.isApproved !== true) {
                      <button type="button" (click)="setTaskApproval(detailTask, true, $event)"
                        class="flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-xs bg-emerald-600 text-white hover:bg-emerald-700"
                        style="color: #ffffff !important;" title="Approve this task for autonomous AI execution">
                        <i class="fa-solid fa-check"></i>
                        <span>Approve</span>
                      </button>
                      }

                      <button type="button" (click)="setTaskApproval(detailTask, false, $event)"
                        class="flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-xs bg-white hover:bg-rose-50 text-rose-700 border border-rose-300"
                        title="Close this task immediately (1 click)">
                        <i class="fa-solid fa-xmark"></i>
                        <span>Close</span>
                      </button>

                      @if (detailTask.isApproved === true) {
                      <button type="button" (click)="setTaskApproval(detailTask, null, $event)"
                        title="Move back to Open"
                        class="px-2.5 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 border border-gray-200 transition-all cursor-pointer bg-white">
                        <i class="fa-solid fa-arrow-rotate-left mr-1"></i> Reset to Open
                      </button>
                      }
                    </div>
                  </div>
                  }
                  } @else if (detailTask.isApproved === true) {
                  <div
                    class="pt-1 border-t border-emerald-200/60 flex items-center gap-1.5 text-[11px] text-emerald-800 font-medium">
                    <i class="fa-solid fa-lock text-xs text-emerald-600"></i>
                    <span>Approval locked: AI is actively executing or ready for test. Cannot be un-approved.</span>
                  </div>
                  }
                </div>
                }

                @if (isEditingTaskDetail()) {
                <!-- RPDR-613: Inline Edit Form for Open Tasks -->
                <div class="bg-white border border-primary/30 rounded-xl p-3.5 space-y-3 shadow-xs">
                  <div class="flex items-center justify-between border-b border-gray-100 pb-2">
                    <span class="text-[10px] font-bold text-primary uppercase tracking-wider flex items-center gap-1.5">
                      <i class="fa-regular fa-pen-to-square"></i>
                      <span>Edit Open Task</span>
                    </span>
                  </div>

                  <!-- Task Type Selector (Bug / Feature) -->
                  <div>
                    <label class="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">Task
                      Type</label>
                    <div class="grid grid-cols-2 gap-2">
                      <button type="button" (click)="editTaskType.set('Bug')"
                        class="px-3 py-1.5 rounded-lg text-xs font-semibold border flex items-center justify-center gap-1.5 transition cursor-pointer"
                        [ngClass]="editTaskType() === 'Bug'
                                  ? 'bg-rose-50 text-rose-700 border-rose-400 ring-1 ring-rose-400/30'
                                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'">
                        <i class="fa-solid fa-bug text-xs"
                          [ngClass]="editTaskType() === 'Bug' ? 'text-rose-600' : 'text-gray-400'"></i>
                        <span>Bug</span>
                      </button>
                      <button type="button" (click)="editTaskType.set('Feature')"
                        class="px-3 py-1.5 rounded-lg text-xs font-semibold border flex items-center justify-center gap-1.5 transition cursor-pointer"
                        [ngClass]="editTaskType() === 'Feature'
                                  ? 'bg-blue-50 text-blue-700 border-blue-400 ring-1 ring-blue-400/30'
                                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'">
                        <i class="fa-solid fa-sparkles text-xs"
                          [ngClass]="editTaskType() === 'Feature' ? 'text-blue-600' : 'text-gray-400'"></i>
                        <span>Feature</span>
                      </button>
                    </div>
                  </div>

                  <!-- Editable Title -->
                  <div>
                    <label class="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">Task
                      Title</label>
                    <input type="text" [ngModel]="editTaskTitle()" (ngModelChange)="editTaskTitle.set($event)"
                      placeholder="Enter task title..."
                      class="w-full text-xs px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                  </div>

                  <!-- Editable Description -->
                  <div>
                    <label class="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">Description
                      (Markdown)</label>
                    <textarea rows="5" [ngModel]="editTaskDescription()"
                      (ngModelChange)="editTaskDescription.set($event)"
                      placeholder="Describe the bug or feature request..."
                      class="w-full text-xs p-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-y"></textarea>
                  </div>

                  <!-- Save / Cancel Buttons -->
                  <div class="flex items-center justify-end gap-2 pt-1">
                    <button type="button" (click)="cancelEditingTask()" [disabled]="isSavingTaskEdit()"
                      class="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 transition cursor-pointer">
                      Cancel
                    </button>
                    <button type="button" (click)="saveTaskEdit(detailTask)"
                      [disabled]="isSavingTaskEdit() || !editTaskTitle().trim()"
                      class="px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-primary hover:bg-hover-primary text-white flex items-center gap-1.5 transition cursor-pointer disabled:opacity-50"
                      style="color: #ffffff !important;">
                      @if (isSavingTaskEdit()) {
                      <i class="fa-solid fa-spinner fa-spin"></i>
                      <span>Saving...</span>
                      } @else {
                      <i class="fa-solid fa-check"></i>
                      <span>Save Changes</span>
                      }
                    </button>
                  </div>
                </div>
                } @else {
                <!-- Title -->
                <div>
                  <span class="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">TASK
                    TITLE</span>
                  <h3 class="text-sm font-bold text-gray-900 leading-snug m-0">
                    {{ detailTask.title }}
                  </h3>
                </div>

                <!-- Full Description (Markdown Preview) -->
                <div class="flex-1 flex flex-col min-h-0">
                  <span
                    class="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">DESCRIPTION</span>
                  <div
                    class="text-xs text-gray-700 bg-white p-3.5 rounded-xl border border-gray-200 flex-1 overflow-y-auto">
                    <rapider-markdown-preview [data]="detailTask.description || 'No description provided.'"
                      [showHeader]="false" [showBorder]="false" [isTransparent]="true"></rapider-markdown-preview>
                  </div>
                </div>
                }

                <!-- Element Pinning / Coordinates Info if attached -->
                @if (detailTask.elementSelector || detailTask.coordinates) {
                <div class="bg-indigo-50/60 border border-indigo-100 rounded-xl p-3 space-y-1 text-xs text-indigo-700">
                  <div class="flex items-center space-x-1.5 font-semibold">
                    <i class="fa-solid fa-location-dot"></i>
                    <span>Attached DOM Element:</span>
                  </div>
                  @if (detailTask.elementSelector) {
                  <div class="font-mono text-[11px] bg-white/70 px-2 py-1 rounded border border-indigo-100 break-all">
                    {{ detailTask.elementSelector }}
                  </div>
                  }
                  @if (detailTask.coordinates) {
                  <div class="text-[11px] text-indigo-600">
                    Offset: X: {{ detailTask.coordinates.x }}px, Y: {{ detailTask.coordinates.y }}px
                  </div>
                  }
                </div>
                }

                <!-- Reporter Metadata -->
                <div class="bg-gray-50 border border-gray-200 rounded-xl p-3 space-y-2">
                  <span class="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">METADATA</span>
                  <div class="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span class="text-gray-400 block text-[11px] mb-1">Reporter:</span>
                      <div class="flex items-center space-x-1.5">
                        @if (detailTask.authorAvatarUrl) {
                        <img [src]="detailTask.authorAvatarUrl" [alt]="detailTask.createdBy"
                          class="w-6 h-6 rounded-full object-cover shrink-0 border border-gray-200" />
                        } @else {
                        <div
                          class="w-6 h-6 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-[8px] font-bold flex items-center justify-center shrink-0 uppercase tracking-tighter">
                          {{ getInitials(detailTask.createdBy) }}
                        </div>
                        }
                        <span class="font-medium text-gray-800 truncate">{{ detailTask.createdBy }}</span>
                      </div>
                    </div>
                    <div>
                      <span class="text-gray-400 block text-[11px] mb-1">Created At:</span>
                      <span class="font-medium text-gray-800">{{ formatTaskDate(detailTask.createdAt) }}</span>
                    </div>
                  </div>
                </div>

                <!-- ITEM 6: QA VERIFICATION SECTION (ONLY SHOW ON 'In Review' FOR APP OWNER / ADMIN) -->
                @if (canApproveTasks() && (detailTask.status === 'In Review' || detailTask.status === 'Ready to Test' ||
                getNormalizedTaskStatus(detailTask) === 'In Review')) {
                <div class="bg-purple-50/70 border border-purple-200 rounded-xl p-3.5 space-y-3">
                  <div class="flex items-center justify-between">
                    <span
                      class="text-[10px] font-bold text-purple-900 uppercase tracking-wider flex items-center gap-1.5">
                      <i class="fa-solid fa-clipboard-check text-purple-600"></i>
                      <span>QA Verification</span>
                    </span>
                    <span
                      class="text-[11px] font-semibold px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full border border-purple-200">
                      In Review
                    </span>
                  </div>

                  <div class="bg-amber-50/90 border border-amber-200 rounded-lg p-3 text-xs text-amber-950 space-y-2.5">
                    <div class="flex items-center gap-1.5 font-bold text-amber-900">
                      <i class="fa-solid fa-circle-info text-amber-600 text-sm"></i>
                      <span>Verify in Preview Harness</span>
                    </div>
                    <p class="text-[11px] text-amber-900/80 leading-relaxed m-0">
                      This application viewer displays the installed version of the app. To test the latest AI changes
                      and verify this task, please open this page in the <strong>App Builder Preview Harness</strong>
                      with the latest develop build.
                    </p>
                    <button type="button" (click)="openInPreviewHarness(detailTask)"
                      class="w-full px-3 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-xs bg-primary text-white hover:opacity-90"
                      style="color: #ffffff !important;">
                      <i class="fa-solid fa-arrow-up-right-from-square"></i>
                      <span>Open in App Builder Preview Harness</span>
                    </button>
                  </div>
                </div>
                }

                <!-- ITEM 7: REPORTER CONFIRMATION SECTION (ONLY SHOW ON 'Released') -->
                @if (detailTask.status === 'Released' || getNormalizedTaskStatus(detailTask) === 'Released') {
                <div class="bg-blue-50/70 border border-blue-200 rounded-xl p-3.5 space-y-3">
                  <div class="flex items-center justify-between">
                    <span
                      class="text-[10px] font-bold text-blue-900 uppercase tracking-wider flex items-center gap-1.5">
                      <i class="fa-solid fa-rocket text-blue-600"></i>
                      <span>Reporter Confirmation</span>
                    </span>
                    <span
                      class="text-[11px] font-semibold px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full border border-blue-300">
                      Released
                    </span>
                  </div>

                  <div class="bg-white border border-blue-100 rounded-lg p-3 text-xs text-blue-950 space-y-2.5">
                    <p class="text-[11px] text-gray-700 leading-relaxed m-0">
                      The fix has been deployed. Please confirm if the issue is resolved or needs to be reopened.
                    </p>
                    @if (isTaskReporter(detailTask) || canApproveTasks()) {
                    <div class="grid grid-cols-2 gap-2 pt-1">
                      <button type="button" (click)="setTaskStatus(detailTask, 'Confirmed', $event)"
                        class="px-3 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-xs bg-emerald-600 text-white hover:bg-emerald-700"
                        style="color: #ffffff !important;">
                        <i class="fa-solid fa-circle-check"></i>
                        <span>Confirm Fix</span>
                      </button>
                      <button type="button" (click)="openRejectModal(detailTask, $event)"
                        class="px-3 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-xs bg-rose-600 text-white hover:bg-rose-700"
                        style="color: #ffffff !important;">
                        <i class="fa-solid fa-rotate-left"></i>
                        <span>Reopen</span>
                      </button>
                    </div>
                    }
                  </div>
                </div>
                }

                <!-- Discussion & Implementation Updates Thread -->
                <div class="bg-white border border-gray-200 rounded-xl p-3.5 space-y-3">
                  <div class="flex items-center justify-between pb-2 border-b border-gray-100">
                    <span
                      class="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                      <i class="fa-regular fa-comments text-xs text-primary"></i>
                      <span>Discussion & Implementation Updates</span>
                    </span>
                    <span class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-600">
                      {{ taskReplies().length }}
                    </span>
                  </div>

                  @if (isLoadingReplies()) {
                  <div class="py-4 text-center text-xs text-gray-400 flex items-center justify-center gap-2">
                    <i class="fa-solid fa-spinner fa-spin text-primary"></i>
                    <span>Loading updates...</span>
                  </div>
                  } @else if (taskReplies().length === 0) {
                  <div
                    class="py-4 text-center text-xs text-gray-400 bg-gray-50/60 rounded-lg border border-dashed border-gray-200">
                    <i class="fa-regular fa-message text-gray-300 text-base block mb-1"></i>
                    <span>No updates or replies yet. Autonomous agents and reviewers post progress notes here.</span>
                  </div>
                  } @else {
                  <div class="space-y-3">
                    @for (reply of taskReplies(); track reply.id) {
                    <div class="bg-gray-50/70 border border-gray-200/80 rounded-xl p-3 space-y-2">
                      <div class="flex items-center justify-between text-xs">
                        <div class="flex items-center space-x-1.5 min-w-0">
                          <div
                            class="w-5 h-5 rounded-full bg-primary/10 text-primary text-[9px] font-bold flex items-center justify-center shrink-0">
                            {{ getInitials(reply.createdBy) }}
                          </div>
                          <span class="font-bold text-gray-800 truncate text-[11px]">{{ reply.createdBy || 'User'
                            }}</span>
                        </div>
                        <span class="text-[10px] text-gray-400 shrink-0">{{ formatTaskDate(reply.createdDate) }}</span>
                      </div>
                      <div
                        class="text-xs text-gray-700 bg-white p-2.5 rounded-lg border border-gray-100 leading-relaxed overflow-x-auto">
                        <rapider-markdown-preview [data]="reply.content" [showHeader]="false" [showBorder]="false"
                          [isTransparent]="true"></rapider-markdown-preview>
                      </div>
                    </div>
                    }
                  </div>
                  }

                  <!-- Post a Reply Input -->
                  <div class="pt-2 border-t border-gray-100 space-y-2">
                    <textarea [(ngModel)]="replyContent" rows="2"
                      placeholder="Write a comment reply or testing feedback..."
                      class="w-full text-xs p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-hidden resize-none bg-gray-50/40"></textarea>
                    <div class="flex items-center justify-end">
                      <button type="button" (click)="submitTaskReply()"
                        [disabled]="isSubmittingReply() || !replyContent().trim()"
                        class="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary hover:bg-hover-primary text-white flex items-center gap-1.5 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-2xs"
                        style="color: #ffffff !important;">
                        @if (isSubmittingReply()) {
                        <i class="fa-solid fa-spinner fa-spin"></i>
                        <span>Posting...</span>
                        } @else {
                        <i class="fa-regular fa-paper-plane"></i>
                        <span>Send Reply</span>
                        }
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            } @else {
            <div class="space-y-4 flex-1 flex flex-col min-w-0">
              <!-- Task Management Stats Summary -->
              <div class="bg-gray-50 border border-gray-200 rounded-xl p-3 space-y-2.5">
                <!-- Two Action Buttons: Report Bug (Drop Bubble) & Request Feature -->
                <div class="grid grid-cols-2 gap-2">
                  <button type="button" (click)="startReportBug()"
                    class="justify-center bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200/90 text-xs font-semibold px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 shadow-2xs transition cursor-pointer"
                    title="Report Bug (Drop Bubble on Micro App)">
                    <i class="fa-solid fa-bug text-xs text-rose-500"></i>
                    <span>Report Bug</span>
                  </button>
                  <button type="button" (click)="openRequestFeatureModal()"
                    class="justify-center bg-primary hover:bg-hover-primary text-white text-xs font-semibold px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 shadow-2xs transition border border-transparent cursor-pointer"
                    title="Request Feature for Micro App" style="color: #ffffff !important;">
                    <i class="fa-solid fa-sparkles text-xs"></i>
                    <span>Request Feature</span>
                  </button>
                </div>

                <div class="grid grid-cols-3 gap-2 text-center pt-1">
                  <button type="button" (click)="setTaskTypeFilter('ALL')"
                    class="rounded-lg p-2 transition-all cursor-pointer flex flex-col items-center justify-center border text-center shadow-2xs"
                    [ngClass]="taskTypeFilter() === 'ALL'
                            ? 'bg-white text-gray-900 border-gray-900 ring-2 ring-gray-900/20 shadow-xs'
                            : 'bg-white text-gray-800 border-gray-200 hover:border-gray-300 hover:bg-gray-50'"
                    i18n-title="@@tasks.filterAllTitle" title="Show all tasks">
                    <span class="text-[10px] uppercase font-semibold block tracking-wider"
                      [ngClass]="taskTypeFilter() === 'ALL' ? 'text-gray-900 font-bold' : 'text-gray-400'"
                      i18n="@@tasks.filterTotal">
                      Total
                    </span>
                    <span class="text-sm font-bold mt-0.5">{{ totalTasksCount() }}</span>
                  </button>

                  <button type="button" (click)="setTaskTypeFilter('Bug')"
                    class="rounded-lg p-2 transition-all cursor-pointer flex flex-col items-center justify-center border text-center shadow-2xs"
                    [ngClass]="taskTypeFilter() === 'Bug'
                            ? 'bg-rose-50 text-rose-700 border-rose-500 ring-2 ring-rose-500/25 shadow-xs'
                            : 'bg-white text-gray-800 border-rose-100 hover:border-rose-200 hover:bg-rose-50/50'"
                    i18n-title="@@tasks.filterBugTitle" title="Filter by bug reports">
                    <span class="text-[10px] uppercase font-semibold block tracking-wider"
                      [ngClass]="taskTypeFilter() === 'Bug' ? 'text-rose-600 font-bold' : 'text-rose-500'"
                      i18n="@@tasks.filterBug">
                      Bug
                    </span>
                    <span class="text-sm font-bold text-rose-600 mt-0.5">{{ bugTasksCount() }}</span>
                  </button>

                  <button type="button" (click)="setTaskTypeFilter('Feature')"
                    class="rounded-lg p-2 transition-all cursor-pointer flex flex-col items-center justify-center border text-center shadow-2xs"
                    [ngClass]="taskTypeFilter() === 'Feature'
                            ? 'bg-blue-50 text-blue-700 border-blue-500 ring-2 ring-blue-500/25 shadow-xs'
                            : 'bg-white text-gray-800 border-blue-100 hover:border-blue-200 hover:bg-blue-50/50'"
                    i18n-title="@@tasks.filterFeatureTitle" title="Filter by feature requests">
                    <span class="text-[10px] uppercase font-semibold block tracking-wider"
                      [ngClass]="taskTypeFilter() === 'Feature' ? 'text-blue-600 font-bold' : 'text-blue-500'"
                      i18n="@@tasks.filterFeature">
                      Feature
                    </span>
                    <span class="text-sm font-bold text-blue-600 mt-0.5">{{ featureTasksCount() }}</span>
                  </button>
                </div>
              </div>

              <!-- Search Input + Multi-Select Filter Dropdown (Same Row) -->
              <div class="flex items-center gap-2">
                <div class="relative flex-1 min-w-0">
                  <i class="fa-solid fa-magnifying-glass absolute left-3 top-2.5 text-xs text-gray-400"></i>
                  <input type="text" [ngModel]="taskSearchQuery()"
                    (ngModelChange)="taskSearchQuery.set($event); loadedTasksLimit.set(20)"
                    i18n-placeholder="@@tasks.searchPlaceholder" placeholder="Search tasks by title or ID..."
                    class="w-full pl-8 pr-8 py-1.5 text-xs bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary shadow-2xs" />
                  @if (taskSearchQuery().trim()) {
                  <button type="button" (click)="clearSearchFilter()"
                    class="absolute right-2.5 top-2 text-gray-400 hover:text-gray-600 transition cursor-pointer text-xs"
                    i18n-title="@@tasks.clearSearch" title="Clear search">
                    <i class="fa-solid fa-xmark"></i>
                  </button>
                  }
                </div>

                <!-- Multi-Select Filter Dropdown (Reported by Me + Statuses) -->
                <button type="button" nz-dropdown [nzDropdownMenu]="taskFilterDropdownMenu" nzTrigger="click"
                  nzPlacement="bottomRight" [(nzVisible)]="isTaskFilterDropdownOpen"
                  class="shrink-0 px-2.5 py-1.5 text-xs font-semibold rounded-lg border shadow-2xs flex items-center gap-1.5 transition cursor-pointer"
                  [ngClass]="activeDropdownFilterCount() > 0
                          ? 'bg-primary/10 text-primary border-primary/40 ring-1 ring-primary/15'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'"
                  title="Filter by Reported by Me and Statuses">
                  <i class="fa-solid fa-sliders text-xs"
                    [ngClass]="activeDropdownFilterCount() > 0 ? 'text-primary' : 'text-gray-500'"></i>
                  <span>Filter</span>
                  @if (activeDropdownFilterCount() > 0) {
                  <span class="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-primary text-white"
                    style="color: #ffffff !important;">
                    {{ activeDropdownFilterCount() }}
                  </span>
                  }
                  <i class="fa-solid fa-chevron-down text-[9px] opacity-70"></i>
                </button>

                <nz-dropdown-menu #taskFilterDropdownMenu="nzDropdownMenu">
                  <div class="bg-white rounded-xl shadow-xl border border-gray-200 py-1.5 w-60 text-xs select-none"
                    (click)="$event.stopPropagation()">
                    <!-- Top Item: Reported by Me (Separate from Statuses) -->
                    <button type="button" (click)="toggleReportedByMeFilter($event)"
                      class="w-full px-3 py-2 flex items-center gap-2.5 hover:bg-gray-50 transition cursor-pointer text-left">
                      <span class="w-4 h-4 rounded border flex items-center justify-center shrink-0 transition"
                        [ngClass]="reportedByMeOnly() ? 'bg-primary border-primary text-white' : 'bg-white border-gray-300'"
                        [style.color]="reportedByMeOnly() ? '#ffffff' : null">
                        @if (reportedByMeOnly()) {
                        <i class="fa-solid fa-check text-[9px]"></i>
                        }
                      </span>
                      <i class="fa-solid fa-user-check text-indigo-500 w-3.5 text-center text-[11px]"></i>
                      <span class="font-semibold text-gray-800 flex-1">Reported by Me</span>
                    </button>

                    <div class="border-t border-gray-100 my-1"></div>

                    <div class="px-3 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                      Statuses
                    </div>

                    <!-- All Statuses Option (Clears individual status checkboxes, preserves Reported by Me) -->
                    <button type="button" (click)="selectAllStatusesFilter($event)"
                      class="w-full px-3 py-1.5 flex items-center gap-2.5 hover:bg-gray-50 transition cursor-pointer text-left">
                      <span class="w-4 h-4 rounded border flex items-center justify-center shrink-0 transition"
                        [ngClass]="selectedTaskStatuses().length === 0 && taskStatusFilter() === 'ALL'
                                ? 'bg-primary border-primary text-white'
                                : 'bg-white border-gray-300'"
                        [style.color]="selectedTaskStatuses().length === 0 && taskStatusFilter() === 'ALL' ? '#ffffff' : null">
                        @if (selectedTaskStatuses().length === 0 && taskStatusFilter() === 'ALL') {
                        <i class="fa-solid fa-check text-[9px]"></i>
                        }
                      </span>
                      <i class="fa-solid fa-bars-staggered text-gray-500 w-3.5 text-center text-[11px]"></i>
                      <span class="font-medium text-gray-800 flex-1">All Statuses</span>
                    </button>

                    <!-- Individual Status Checkboxes (OR logic) -->
                    @for (st of taskStatusFilterMeta; track st.value) {
                    <button type="button" (click)="toggleTaskStatusCheckbox(st.value, $event)"
                      class="w-full px-3 py-1.5 flex items-center gap-2.5 hover:bg-gray-50 transition cursor-pointer text-left">
                      <span class="w-4 h-4 rounded border flex items-center justify-center shrink-0 transition"
                        [ngClass]="selectedTaskStatuses().includes(st.value)
                                  ? 'bg-primary border-primary text-white'
                                  : 'bg-white border-gray-300'"
                        [style.color]="selectedTaskStatuses().includes(st.value) ? '#ffffff' : null">
                        @if (selectedTaskStatuses().includes(st.value)) {
                        <i class="fa-solid fa-check text-[9px]"></i>
                        }
                      </span>
                      <i [class]="st.iconClass + ' ' + st.iconColorClass + ' w-3.5 text-center text-[11px]'"></i>
                      <span class="font-medium text-gray-700 flex-1">{{ st.label }}</span>
                    </button>
                    }
                  </div>
                </nz-dropdown-menu>
              </div>

              <!-- Page Scope Filter Controls -->
              <div class="space-y-1">
                <span class="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">
                  PAGE SCOPE
                </span>
                <div class="flex items-center gap-1.5 flex-wrap">
                  <button type="button" (click)="setTaskPageFilter('CURRENT')"
                    class="px-2.5 py-1 text-xs font-medium rounded-lg transition-all cursor-pointer shadow-2xs border flex items-center gap-1.5"
                    [ngClass]="taskPageFilter() === 'CURRENT'
                            ? 'bg-primary !text-white text-white border-primary shadow-xs ring-1 ring-primary/25 font-bold'
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-900'"
                    [style.color]="taskPageFilter() === 'CURRENT' ? '#ffffff' : null"
                    title="Show tasks for active page only">
                    <i class="fa-solid fa-file-code text-[10px]"
                      [ngClass]="taskPageFilter() === 'CURRENT' ? '!text-white text-white' : 'text-primary'"></i>
                    <span [style.color]="taskPageFilter() === 'CURRENT' ? '#ffffff' : null">Current Page</span>
                    <span class="text-[10px] px-1.5 py-0.2 rounded-full font-bold ml-0.5"
                      [ngClass]="taskPageFilter() === 'CURRENT' ? 'bg-white/20 !text-white text-white' : 'bg-gray-100 text-gray-600'">
                      {{ activePageTasksCount() }}
                    </span>
                  </button>

                  <button type="button" (click)="setTaskPageFilter('ALL')"
                    class="px-2.5 py-1 text-xs font-medium rounded-lg transition-all cursor-pointer shadow-2xs border flex items-center gap-1.5"
                    [ngClass]="taskPageFilter() === 'ALL'
                            ? 'bg-white text-gray-900 border-gray-900 shadow-xs ring-1 ring-gray-900/20 font-bold'
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-900'"
                    title="Show tasks across all pages">
                    <i class="fa-solid fa-layer-group text-[10px]"
                      [ngClass]="taskPageFilter() === 'ALL' ? 'text-gray-900' : 'text-gray-400'"></i>
                    <span>All Pages</span>
                    <span class="text-[10px] px-1.5 py-0.2 rounded-full font-bold ml-0.5"
                      [ngClass]="taskPageFilter() === 'ALL' ? 'bg-gray-200 text-gray-900' : 'bg-gray-100 text-gray-600'">
                      {{ totalAppTasksCount() }}
                    </span>
                  </button>
                </div>
              </div>

              <!-- Active Filter Selection Chips & Clear Button -->
              @if (hasActiveFilters()) {
              <div class="flex items-center gap-1.5 flex-wrap p-2 bg-gray-50 border border-gray-200 rounded-lg text-xs">
                <span class="text-[10px] font-bold text-gray-400 uppercase tracking-wider mr-0.5"
                  i18n="@@tasks.selectedFiltersLabel">
                  Selected:
                </span>

                <!-- Removable Reported by Me Chip -->
                @if (reportedByMeOnly()) {
                <span
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium shadow-2xs border bg-indigo-50 text-indigo-700 border-indigo-200">
                  <i class="fa-solid fa-user-check text-[9px]"></i>
                  <span>Reported by Me</span>
                  <button type="button" (click)="clearReportedByMeFilter()"
                    class="opacity-60 hover:opacity-100 ml-0.5 cursor-pointer focus:outline-none"
                    title="Remove Reported by Me filter">
                    <i class="fa-solid fa-xmark text-[10px]"></i>
                  </button>
                </span>
                }

                <!-- Removable Type Chip -->
                @if (taskTypeFilter() !== 'ALL') {
                <span
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium shadow-2xs border"
                  [ngClass]="taskTypeFilter() === 'Bug' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-blue-50 text-blue-700 border-blue-200'">
                  <i
                    [class]="taskTypeFilter() === 'Bug' ? 'fa-solid fa-bug text-[9px]' : 'fa-solid fa-sparkles text-[9px]'"></i>
                  <span>{{ taskTypeFilter() }}</span>
                  <button type="button" (click)="clearTypeFilter()"
                    class="opacity-60 hover:opacity-100 ml-0.5 cursor-pointer focus:outline-none"
                    i18n-title="@@tasks.removeTypeFilter" title="Remove type filter">
                    <i class="fa-solid fa-xmark text-[10px]"></i>
                  </button>
                </span>
                }

                <!-- Removable Page Filter Chip (when showing All Pages) -->
                @if (taskPageFilter() === 'ALL') {
                <span
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium shadow-2xs border bg-purple-50 text-purple-800 border-purple-200">
                  <i class="fa-solid fa-layer-group text-[9px]"></i>
                  <span>All Pages</span>
                  <button type="button" (click)="clearPageFilter()"
                    class="opacity-60 hover:opacity-100 ml-0.5 cursor-pointer focus:outline-none"
                    title="Filter current page only">
                    <i class="fa-solid fa-xmark text-[10px]"></i>
                  </button>
                </span>
                }

                <!-- Removable Multi-Select Status Chips -->
                @for (st of selectedTaskStatuses(); track st) {
                <span
                  class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium shadow-2xs border"
                  [ngClass]="getTaskStatusClass(st)">
                  <span class="w-1.5 h-1.5 rounded-full" [ngClass]="getTaskStatusDotClass(st)"></span>
                  <span>{{ st }}</span>
                  <button type="button" (click)="removeTaskStatusFilter(st)"
                    class="opacity-60 hover:opacity-100 ml-0.5 cursor-pointer focus:outline-none"
                    title="Remove status filter">
                    <i class="fa-solid fa-xmark text-[10px]"></i>
                  </button>
                </span>
                }

                <!-- Fallback Single Status Chip -->
                @if (selectedTaskStatuses().length === 0 && taskStatusFilter() !== 'ALL') {
                <span
                  class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium shadow-2xs border"
                  [ngClass]="getTaskStatusClass(taskStatusFilter())">
                  <span class="w-1.5 h-1.5 rounded-full" [ngClass]="getTaskStatusDotClass(taskStatusFilter())"></span>
                  <span>{{ taskStatusFilter() }}</span>
                  <button type="button" (click)="clearStatusFilter()"
                    class="opacity-60 hover:opacity-100 ml-0.5 cursor-pointer focus:outline-none"
                    i18n-title="@@tasks.removeStatusFilter" title="Remove status filter">
                    <i class="fa-solid fa-xmark text-[10px]"></i>
                  </button>
                </span>
                }

                <!-- Removable Search Chip -->
                @if (taskSearchQuery().trim()) {
                <span
                  class="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-800 rounded-md border border-gray-200 text-[11px] font-medium shadow-2xs">
                  <i class="fa-solid fa-magnifying-glass text-[9px] text-gray-400"></i>
                  <span class="max-w-[120px] truncate">"{{ taskSearchQuery().trim() }}"</span>
                  <button type="button" (click)="clearSearchFilter()"
                    class="text-gray-400 hover:text-gray-700 ml-0.5 cursor-pointer focus:outline-none"
                    i18n-title="@@tasks.removeSearchFilter" title="Remove search filter">
                    <i class="fa-solid fa-xmark text-[10px]"></i>
                  </button>
                </span>
                }

                <!-- Clear Button -->
                <button type="button" (click)="resetAllFilters()"
                  class="text-[11px] text-rose-600 hover:text-rose-700 font-semibold cursor-pointer ml-auto flex items-center gap-1 px-2 py-0.5 rounded bg-rose-50/80 hover:bg-rose-100 border border-rose-200/80 transition shadow-2xs"
                  i18n-title="@@tasks.clearAllFiltersTitle" title="Clear all filters">
                  <i class="fa-solid fa-xmark text-[10px]"></i>
                  <span i18n="@@tasks.clearFilters">Clear</span>
                </button>
              </div>
              }

              <!-- Task Cards Container -->
              <div class="space-y-2.5 flex-1 overflow-y-auto overflow-x-hidden pr-0.5">
                @if (isLoadingComments()) {
                <div
                  class="text-center py-10 px-4 bg-gray-50 border border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center gap-2">
                  <i class="fa-solid fa-circle-notch fa-spin text-xl text-primary"></i>
                  <p class="text-xs font-medium text-gray-600" i18n="@@tasks.loadingTasks">Loading tasks...</p>
                </div>
                } @else if (displayedTasks().length === 0) {
                <div class="text-center py-10 px-4 bg-gray-50 border border-dashed border-gray-200 rounded-xl">
                  <i class="fa-regular fa-folder-open text-2xl text-gray-400 mb-2"></i>
                  <p class="text-xs font-medium text-gray-600" i18n="@@tasks.noTasksFound">No tasks found</p>
                  <p class="text-[11px] text-gray-400">
                    @if (taskPageFilter() === 'CURRENT') {
                    No tasks or bugs reported for this page ({{ getActivePageName() || 'Active Page' }}).
                    } @else {
                    Try adjusting your filters or create a task
                    }
                  </p>
                  @if (taskPageFilter() === 'CURRENT' && totalAppTasksCount() > 0) {
                  <button type="button" (click)="setTaskPageFilter('ALL')"
                    class="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-200 rounded-lg text-xs font-medium text-primary hover:bg-gray-50 shadow-2xs transition cursor-pointer">
                    <i class="fa-solid fa-layer-group text-[10px]"></i>
                    <span>View All App Tasks ({{ totalAppTasksCount() }})</span>
                  </button>
                  }
                  @if (hasActiveFilters()) {
                  <button type="button" (click)="resetAllFilters()"
                    class="mt-3 inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-200 rounded-lg text-xs font-medium text-primary hover:bg-gray-50 shadow-2xs transition cursor-pointer">
                    <i class="fa-solid fa-arrow-rotate-left text-[10px]"></i>
                    <span i18n="@@tasks.resetFilters">Reset filters</span>
                  </button>
                  }
                </div>
                } @else {
                @for (task of displayedTasks(); track task.rawId) {
                <div [id]="'task-card-' + task.rawId" (click)="selectTask(task, $event)"
                  (dblclick)="openTaskDetails(task)"
                  class="task-card bg-white border border-gray-200 hover:border-gray-300 rounded-xl p-3.5 shadow-sm hover:shadow-md transition cursor-pointer group space-y-2 relative min-w-0"
                  [class.task-card-active]="selectedTaskId() === task.rawId">
                  <!-- Top Metadata Row: Number Badge + ID on Left; Badges + Actions on Right (wraps to 2nd row on small screen) -->
                  <div class="flex items-center justify-between flex-wrap gap-2">
                    <!-- Left: Number Circle + Task ID -->
                    <div class="flex items-center space-x-2 shrink-0">
                      <span
                        class="w-6 h-6 rounded-full text-white text-[11px] font-bold flex items-center justify-center shrink-0 shadow-xs transition-colors"
                        [ngClass]="selectedTaskId() === task.rawId ? 'bg-amber-500 ring-2 ring-amber-300' : (task.type === 'Bug' ? 'bg-[orangered]' : 'bg-blue-600')"
                        [style.font-size]="(task.bubbleNumber || 0) > 99 ? 'x-small' : null"
                        [title]="task.type + ' #' + formatBubbleNumber(task.bubbleNumber) + ((task.bubbleNumber || 0) > 999 ? ' (' + task.bubbleNumber + ')' : '')">
                        {{ formatBubbleNumber(task.bubbleNumber) }}
                      </span>
                      <span class="text-[11px] font-mono font-bold text-gray-700">{{ task.id }}</span>
                      @if (taskPageFilter() === 'ALL' || !isTaskOnActivePage(task)) {
                      <span
                        class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-200"
                        [title]="'Page: ' + getTaskPageName(task)">
                        <i class="fa-solid fa-file-code text-[9px] text-slate-400"></i>
                        <span class="max-w-[100px] truncate">{{ getTaskPageName(task) }}</span>
                      </span>
                      }
                      @if (selectedTaskId() === task.rawId) {
                      <span
                        class="inline-flex items-center gap-1 px-1.5 py-0.2 rounded-full text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-300 shadow-2xs">
                        <i class="fa-solid fa-bolt text-[8px] fa-shake"></i> Active
                      </span>
                      }
                    </div>

                    <!-- Right: Type Tag, Status Tag, and Actions (wraps down to 2nd row when width is constrained) -->
                    <div class="flex items-center gap-1.5 flex-wrap">
                      @if (task.type === 'Bug') {
                      <span
                        class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-50 text-rose-600 border border-rose-200/80">
                        <i class="fa-solid fa-circle-exclamation text-[9px] mr-1"></i> Bug
                      </span>
                      } @else {
                      <span
                        class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-600 border border-blue-200/80">
                        <i class="fa-solid fa-sparkles text-[9px] mr-1"></i> Feature
                      </span>
                      }

                      <!-- Single Unified Status Badge -->
                      <span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border"
                        [ngClass]="getTaskStatusClass(task.status)">
                        @if (task.status === 'In Review' || task.status === 'Ready to Test') {
                        <i class="fa-solid fa-clipboard-check text-[9px] mr-1"></i>
                        } @else if (task.status === 'Ready for Release') {
                        <i class="fa-solid fa-tag text-[9px] mr-1"></i>
                        } @else if (task.status === 'Released') {
                        <i class="fa-solid fa-rocket text-[9px] mr-1"></i>
                        } @else if (task.status === 'Confirmed' || task.status === 'Resolved') {
                        <i class="fa-solid fa-circle-check text-[9px] mr-1"></i>
                        } @else if (task.status === 'Reopened' || task.status === 'Reporter Rejected') {
                        <i class="fa-solid fa-rotate-left text-[9px] mr-1"></i>
                        } @else if (task.status === 'Accepted' || task.status === 'Approved' || task.status ===
                        'approved') {
                        <i class="fa-solid fa-check text-[9px] mr-1"></i>
                        } @else if (task.status === 'In Progress') {
                        <i class="fa-solid fa-clock text-[9px] mr-1"></i>
                        } @else if (task.status === 'Closed') {
                        <i class="fa-solid fa-circle-xmark text-[9px] mr-1"></i>
                        }
                        {{ getDisplayStatus(task.status) }}
                      </span>

                      <!-- Action Buttons (Approve, Close, Reopen, Delete) -->
                      @if ((canApproveTasks() && (getNormalizedTaskStatus(task) === 'Open' ||
                      getNormalizedTaskStatus(task) === 'Reopened' || getNormalizedTaskStatus(task) === 'Closed')) ||
                      task.isApproved !== true) {
                      <div
                        class="flex items-center space-x-1 pl-1 border-l border-gray-200 opacity-80 group-hover:opacity-100 transition shrink-0">
                        <!-- Quick Approve & Close Buttons for Owner / Admin (When Open or Reopened) -->
                        @if (canApproveTasks() && (getNormalizedTaskStatus(task) === 'Open' ||
                        getNormalizedTaskStatus(task) === 'Reopened')) {
                        <button type="button" (click)="setTaskApproval(task, true, $event)"
                          title="Approve Task for AI execution"
                          class="p-1 rounded transition cursor-pointer text-gray-400 hover:text-emerald-600 hover:bg-emerald-50">
                          <i class="fa-solid fa-check text-xs"></i>
                        </button>
                        <button type="button" (click)="setTaskApproval(task, false, $event)" title="Close Task"
                          class="p-1 rounded transition cursor-pointer text-gray-400 hover:text-rose-600 hover:bg-rose-50">
                          <i class="fa-solid fa-xmark text-xs"></i>
                        </button>
                        }

                        <!-- Quick Reopen Button for Owner / Admin (When Closed) -->
                        @if (canApproveTasks() && getNormalizedTaskStatus(task) === 'Closed') {
                        <button type="button" (click)="setTaskApproval(task, null, $event)" title="Reopen Task"
                          class="p-1 rounded transition cursor-pointer text-gray-400 hover:text-primary hover:bg-blue-50">
                          <i class="fa-solid fa-rotate-left text-xs"></i>
                        </button>
                        }

                        <!-- Edit Open Task (Reporter only while Open) -->
                        @if (canEditTask(task)) {
                        <button type="button" (click)="startEditingTask(task, $event)" title="Edit Task"
                          class="p-1 text-gray-400 hover:text-primary hover:bg-blue-50 rounded transition cursor-pointer">
                          <i class="fa-regular fa-pen-to-square text-xs"></i>
                        </button>
                        }

                        <!-- Delete Task (Removed when task is Approved) -->
                        @if (task.isApproved !== true) {
                        <button type="button" (click)="confirmDeleteTask(task, $event)" title="Delete Task"
                          class="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition cursor-pointer">
                          <i class="fa-regular fa-trash-can text-xs"></i>
                        </button>
                        }
                      </div>
                      }
                    </div>
                  </div>

                  <!-- Card Title -->
                  <h4 class="text-xs font-bold text-gray-900 group-hover:text-primary transition line-clamp-2 m-0">
                    {{ task.title }}
                  </h4>

                  <!-- Description Preview with Rendered Markdown -->
                  @if (task.description) {
                  <div class="relative">
                    <div
                      class="task-card-markdown text-[11px] text-gray-600 font-normal leading-relaxed m-0 overflow-hidden transition-all duration-200"
                      [ngClass]="isTaskDescriptionExpanded(task.rawId) ? 'max-h-none' : 'max-h-[6.5rem]'">
                      <rapider-markdown-preview [data]="task.description" [showHeader]="false" [showBorder]="false"
                        [isTransparent]="true" viewMode="compact"></rapider-markdown-preview>
                    </div>

                    @if (!isTaskDescriptionExpanded(task.rawId) && isDescriptionLong(task.description)) {
                    <div
                      class="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none">
                    </div>
                    }

                    @if (isDescriptionLong(task.description)) {
                    <button type="button" (click)="toggleTaskDescription(task, $event)"
                      class="text-[11px] font-semibold text-primary hover:text-hover-primary inline-flex items-center gap-1 cursor-pointer transition focus:outline-none mt-1">
                      <span>{{ isTaskDescriptionExpanded(task.rawId) ? 'Show less' : 'Read more' }}</span>
                      <i
                        [class]="isTaskDescriptionExpanded(task.rawId) ? 'fa-solid fa-chevron-up text-[9px]' : 'fa-solid fa-chevron-down text-[9px]'"></i>
                    </button>
                    }
                  </div>
                  }

                  <!-- Replies Count & Last Repliers Summary -->
                  @if (task.replyCount && task.replyCount > 0) {
                  <div class="flex items-center gap-1.5 text-[11px] text-gray-500 pt-1 border-t border-gray-100/80">
                    <i class="fa-regular fa-comment-dots text-primary text-xs shrink-0"></i>
                    <span class="font-semibold text-gray-700">
                      {{ task.replyCount }} {{ task.replyCount === 1 ? 'Reply' : 'Replies' }}
                    </span>
                    @if (task.lastReplierNames && task.lastReplierNames.length > 0) {
                    <span class="text-gray-400 truncate text-[10px]">
                      by <span class="text-gray-600 font-medium">{{ formatReplierNames(task.lastReplierNames) }}</span>
                    </span>
                    }
                  </div>
                  }

                  <!-- Footer Info: Reporter Avatar + Created Date (left) & Always-Visible View Details Button (right) -->
                  <div
                    class="flex items-center justify-between pt-2 border-t border-gray-100 text-[10px] text-gray-400 min-h-[34px] gap-2">
                    <div class="flex items-center space-x-2 min-w-0">
                      @if (task.authorAvatarUrl) {
                      <img [src]="task.authorAvatarUrl" [alt]="task.createdBy"
                        class="w-6 h-6 rounded-full object-cover shrink-0 border border-gray-200" />
                      } @else {
                      <div
                        class="w-6 h-6 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-[8px] font-bold flex items-center justify-center shrink-0 uppercase tracking-tighter">
                        {{ getInitials(task.createdBy) }}
                      </div>
                      }
                      <div class="flex flex-col min-w-0 leading-tight">
                        <span class="truncate font-medium text-gray-600 text-[11px]">{{ task.createdBy }}</span>
                        <span class="truncate text-[10px] text-gray-400">{{ formatTaskDate(task.createdAt) }}</span>
                      </div>
                    </div>

                    <!-- Right: Always-Visible Primary "View Details ->" Button -->
                    <div class="flex items-center shrink-0">
                      <button type="button" (click)="openTaskDetails(task); $event.stopPropagation()"
                        class="inline-flex items-center gap-1.5 bg-primary hover:bg-hover-primary !text-white text-white font-semibold text-[11px] px-2.5 py-1 rounded-lg shadow-2xs transition-all cursor-pointer border-none"
                        style="color: #ffffff !important;" title="View Details">
                        <span>View Details</span>
                        <i class="fa-solid fa-arrow-right text-[10px]"></i>
                      </button>
                    </div>
                  </div>
                </div>
                }

                <!-- Load More Button -->
                @if (hasMoreComments()) {
                <div class="pt-2 pb-2">
                  <button type="button" (click)="loadMoreTasks()" [disabled]="isLoadingMoreComments()"
                    class="w-full py-2 px-3 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 border border-gray-200 text-gray-700 hover:text-primary rounded-xl text-xs font-semibold shadow-sm transition flex items-center justify-center space-x-1.5 cursor-pointer disabled:cursor-not-allowed"
                    i18n-title="@@tasks.loadMoreTitle" title="Load more tasks">
                    @if (isLoadingMoreComments()) {
                    <i class="fa-solid fa-circle-notch fa-spin text-xs text-primary"></i>
                    <span i18n="@@tasks.loadingMore">Loading more tasks...</span>
                    } @else {
                    <i class="fa-solid fa-arrow-down text-[10px]"></i>
                    <span i18n="@@tasks.loadMore">Load More</span>
                    }
                  </button>
                </div>
                }
                }
              </div>
            </div>
            }
            }

            @if (activeSidebarTab() === 'E2E Tests') {
            <div class="flex flex-col gap-4 h-full">
              <div class="bg-white rounded-lg shadow-sm border border-gray-100 flex flex-col">
                <!-- Header -->
                <div
                  class="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded-lg transition-colors"
                  [class.rounded-b-none]="!isSettingsCollapsed()"
                  (click)="isSettingsCollapsed.set(!isSettingsCollapsed())">
                  <h4 class="font-bold text-gray-800 m-0 flex items-center gap-2 text-sm">
                    <i class="fas fa-flask text-primary"></i> E2E Test Engine
                  </h4>
                  <div class="flex items-center gap-2">
                    @if (isSettingsCollapsed()) {
                    @if (isTestRunning()) {
                    <button (click)="stopE2ETests(); $event.stopPropagation()" nz-button nzType="primary" nzDanger
                      nzSize="small"
                      class="bg-red-600 hover:bg-red-700 border-none shadow-sm flex items-center gap-1 font-bold px-3 cursor-pointer !text-white text-white rounded transition-transform hover:scale-105"
                      style="color: #ffffff !important;">
                      <i class="fas fa-stop text-[10px]"></i>
                      Stop
                    </button>
                    }
                    <button (click)="runE2ETests(); $event.stopPropagation()" nz-button nzType="primary" nzSize="small"
                      [disabled]="isTestRunning()"
                      class="bg-primary hover:bg-hover-primary border-none shadow-sm flex items-center gap-1 font-bold px-3 cursor-pointer !text-white text-white rounded transition-transform hover:scale-105"
                      style="color: #ffffff !important;">
                      <i class="fas text-[10px]" [class.fa-spinner]="isTestRunning()" [class.fa-spin]="isTestRunning()"
                        [class.fa-play]="!isTestRunning()"></i>
                      {{ isTestRunning() ? 'Running' : 'Run' }}
                    </button>
                    }
                    <i class="fas text-gray-400 text-xs transition-transform"
                      [class.rotate-180]="!isSettingsCollapsed()" class="fa-chevron-down"></i>
                  </div>
                </div>

                @if (!isSettingsCollapsed()) {
                <div class="p-4 pt-2 border-t border-gray-50 flex flex-col items-center justify-center">
                  <p class="text-[10px] text-gray-500 mb-4 text-center w-full">
                    Fast, schema-driven internal test execution.
                  </p>

                  <div
                    class="flex items-center gap-2 mb-3 bg-gray-50 px-3 py-2 rounded border border-gray-100 w-full justify-between">
                    <div class="flex flex-col">
                      <span class="text-xs font-bold text-gray-700">Moving Mouse &amp; Pacing</span>
                      <span class="text-[10px] text-gray-500">Animate virtual cursor &amp; UI action highlights</span>
                    </div>
                    <nz-switch [ngModel]="virtualMouse()" (ngModelChange)="virtualMouse.set($event)" nzSize="small">
                    </nz-switch>
                  </div>

                  <div
                    class="flex items-center gap-2 mb-3 bg-gray-50 px-3 py-2 rounded border border-gray-100 w-full justify-between">
                    <div class="flex flex-col">
                      <span class="text-xs font-bold text-gray-700">Test Active Page Only</span>
                      <span class="text-[10px] text-gray-500">Only test current page ({{ activePageRoute() ||
                        activePageId() || 'active' }})</span>
                    </div>
                    <nz-switch [ngModel]="testActivePageOnly()" (ngModelChange)="testActivePageOnly.set($event)"
                      nzSize="small">
                    </nz-switch>
                  </div>

                  <div
                    class="flex items-center gap-2 mb-3 bg-gray-50 px-3 py-2 rounded border border-gray-100 w-full justify-between">
                    <div class="flex flex-col">
                      <span class="text-xs font-bold text-gray-700">Capture All Screenshots</span>
                      <span class="text-[10px] text-gray-500">Requires screen share permission</span>
                    </div>
                    <nz-switch [ngModel]="captureScreenshots()" (ngModelChange)="onCaptureAllChange($event)"
                      nzSize="small">
                    </nz-switch>
                  </div>

                  <div
                    class="flex items-center gap-2 mb-3 bg-gray-50 px-3 py-2 rounded border border-gray-100 w-full justify-between">
                    <div class="flex flex-col">
                      <span class="text-xs font-bold text-gray-700">Capture Screenshots Only On Failure</span>
                      <span class="text-[10px] text-gray-500">Only captures on test failure</span>
                    </div>
                    <nz-switch [ngModel]="captureScreenshotsOnFailure()"
                      (ngModelChange)="onCaptureFailureChange($event)" nzSize="small">
                    </nz-switch>
                  </div>

                  <div
                    class="flex items-center gap-2 mb-3 bg-gray-50 px-3 py-2 rounded border border-gray-100 w-full justify-between">
                    <div class="flex flex-col">
                      <span class="text-xs font-bold text-gray-700">Record Video (Optional)</span>
                      <span class="text-[10px] text-gray-500">Records a video of the test execution</span>
                    </div>
                    <nz-switch [ngModel]="recordVideo()" (ngModelChange)="recordVideo.set($event)" nzSize="small">
                    </nz-switch>
                  </div>

                  <div class="flex items-center gap-2 mt-2 w-full justify-center">
                    @if (isTestRunning()) {
                    <button (click)="stopE2ETests()" nz-button nzType="primary" nzDanger
                      class="bg-red-600 hover:bg-red-700 border-none shadow-sm flex items-center gap-2 font-bold px-5 cursor-pointer !text-white text-white h-9 rounded-lg transition-transform hover:scale-105 text-sm"
                      style="color: #ffffff !important;">
                      <i class="fas fa-stop"></i>
                      Stop
                    </button>
                    }
                    <button (click)="runE2ETests()" nz-button nzType="primary" [disabled]="isTestRunning()"
                      class="bg-primary hover:bg-hover-primary border-none shadow-sm flex items-center gap-2 font-bold px-6 cursor-pointer !text-white text-white h-9 rounded-lg transition-transform hover:scale-105 text-sm"
                      style="color: #ffffff !important;">
                      <i class="fas" [class.fa-spinner]="isTestRunning()" [class.fa-spin]="isTestRunning()"
                        [class.fa-play]="!isTestRunning()"></i>
                      {{ isTestRunning() ? 'Running...' : 'Run Tests' }}
                    </button>
                  </div>
                </div>
                }
              </div>

              <div
                class="flex-1 bg-white rounded-lg p-6 overflow-y-auto border border-gray-100 shadow-inner min-h-[300px] e2e-timeline-container">
                <h4 class="font-bold text-gray-700 mb-4 flex items-center gap-2">
                  <i class="fas fa-terminal text-gray-400"></i> Current Execution
                </h4>
                @if (testLogs().length === 0) {
                <div class="text-gray-400 text-center mt-10 italic text-sm">
                  Test output will appear here...
                </div>
                }
                <nz-timeline>
                  @for (log of testLogs(); track $index) {
                  @if (log.type !== 'system' && log.type !== 'suite') {
                  <nz-timeline-item [nzColor]="log.success === null ? 'blue' : (log.success ? 'green' : 'red')"
                    [nzDot]="customDotTemplate">
                    <ng-template #customDotTemplate>
                      <div
                        class="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white shadow-sm ring-4 ring-white"
                        [ngClass]="log.success === null ? 'bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.8)]' : (log.success ? 'bg-green-500' : 'bg-red-500')">
                        @if (log.success === null) {
                        <i class="fas fa-spinner fa-spin"></i>
                        } @else {
                        {{ log.stepNumber || '-' }}
                        }
                      </div>
                    </ng-template>

                    <div [id]="log.success === null ? 'active-test-card' : ''"
                      class="flex flex-col mb-2 shadow-sm transition-shadow p-3 rounded-lg border ml-2 relative"
                      [ngClass]="log.success === null ? 'bg-blue-50 border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.2)] animate-pulse' : 'bg-white border-gray-200 hover:shadow'">
                      <div class="flex items-start justify-between mb-1 gap-2">
                        <strong class="text-sm font-bold leading-tight"
                          [ngClass]="log.success === null ? 'text-blue-800' : 'text-gray-800'">{{ log.stepName
                          }}</strong>
                        <nz-tag [nzColor]="
                                      log.success === null ? 'processing' : (log.success ? 'success' : 'error')
                                    " class="m-0 text-[10px] uppercase font-bold">{{
                          log.success === null ? 'TESTING...' : (log.success ? 'PASSED' : 'FAILED')
                          }}</nz-tag>
                      </div>
                      @if (log.explanation) {
                      <p class="text-xs text-gray-500 mb-2 leading-relaxed">
                        {{ log.explanation }}
                      </p>
                      }
                      @if (!log.success && log.error) {
                      <div
                        class="text-[11px] text-red-600 font-mono mt-1 mb-2 bg-red-50 p-2 rounded border border-red-100 whitespace-pre-wrap">
                        {{ log.error }}
                      </div>
                      }
                      <div class="text-[10px] mt-auto pt-2 border-t border-gray-50 flex items-center justify-between">
                        <div class="flex items-center gap-2">
                          @if (log.action) {
                          <nz-tag [nzColor]="'blue'"
                            class="m-0 text-[9px] uppercase font-bold border-none bg-blue-50 text-blue-600"><i
                              class="fas fa-bolt mr-1"></i>{{ log.action }}</nz-tag>
                          }
                        </div>
                        <div class="text-gray-400 flex items-center gap-1 font-medium">
                          <i class="far fa-clock"></i>
                          {{ log.timestamp | date: 'HH:mm:ss.SSS' }}
                        </div>
                      </div>
                    </div>
                  </nz-timeline-item>
                  } @else {
                  <nz-timeline-item [nzColor]="log.type === 'system' ? 'gray' : 'blue'">
                    @if (log.type === 'system') {
                    <div class="text-[11px] text-gray-500 italic flex items-center justify-between">
                      <span>{{ log.message }}</span>
                      <span class="opacity-60">{{
                        log.timestamp | date: 'HH:mm:ss.SSS'
                        }}</span>
                    </div>
                    } @else if (log.type === 'suite') {
                    <div
                      class="font-bold text-gray-800 text-sm mt-3 mb-1 border-b border-gray-100 pb-1 flex items-center justify-between">
                      <span><i class="fas fa-file-lines text-primary mr-1"></i>
                        Page: {{ log.suiteName }}</span>
                      <span class="text-[10px] text-gray-400 font-normal">{{ log.timestamp | date: 'HH:mm:ss' }}</span>
                    </div>
                    }
                  </nz-timeline-item>
                  }
                  }
                </nz-timeline>

                @if (testLogs().length > 0 && !isTestRunning()) {
                <div class="flex items-center justify-end gap-3 mt-4 pt-4 border-t border-gray-100 flex-wrap">
                  <button (click)="previewCurrentTestReport()" nz-button
                    class="bg-white border border-gray-200 shadow-sm flex items-center gap-2 font-bold px-4 cursor-pointer text-primary h-9 rounded-lg transition-colors hover:bg-gray-50"
                    title="Preview Test Report">
                    <i class="fas fa-eye"></i> View Report
                  </button>
                  <button (click)="downloadTestReport()" nz-button
                    class="bg-white border border-gray-200 shadow-sm flex items-center gap-2 font-bold px-4 cursor-pointer text-gray-700 h-9 rounded-lg transition-colors hover:bg-gray-50"
                    title="Download Report as Markdown">
                    <i class="fas fa-download"></i> Download Report
                  </button>
                  <button (click)="saveTestReport()" nz-button nzType="primary" [nzLoading]="isSavingReport()"
                    class="!bg-primary hover:!bg-primary/90 border-none shadow-sm flex items-center gap-2 font-bold px-4 cursor-pointer !text-white h-9 rounded-lg transition-transform hover:scale-105"
                    style="color: #ffffff !important; background-color: var(--primary, #1677ff) !important;"
                    title="Save Report to Cloud">
                    <i class="fas fa-cloud-upload-alt !text-white" style="color: #ffffff !important;"></i>
                    <span class="!text-white font-medium" style="color: #ffffff !important;">Save to Cloud</span>
                  </button>
                </div>
                }

                <!-- Saved Reports List -->
                <h4 class="font-bold text-gray-700 mt-8 mb-4 flex items-center gap-2">
                  <i class="fas fa-history text-gray-400"></i> Saved Reports
                </h4>
                @if (savedReports().length === 0) {
                <div class="text-gray-400 text-center italic text-sm">
                  No saved reports found.
                </div>
                }

                <div class="flex flex-col gap-3">
                  @for (report of savedReports(); track report.id) {
                  <div class="bg-gray-50 border border-gray-200 p-3 rounded-lg shadow-sm flex flex-col gap-2">
                    <div class="flex justify-between items-center">
                      <span class="font-bold text-gray-800 text-sm flex items-center gap-1.5">
                        @if (report.value?.success !== undefined) {
                        <i class="fas" [class.fa-check-circle]="report.value?.success"
                          [class.text-green-500]="report.value?.success"
                          [class.fa-times-circle]="!report.value?.success"
                          [class.text-red-500]="!report.value?.success"></i>
                        } @else {
                        <i class="fas fa-file-alt text-gray-400"></i>
                        }
                        {{ report.name }}
                      </span>
                      <span class="text-xs text-gray-500">{{ report.createdDate | date: 'medium' }}</span>
                    </div>
                    @if (report.value) {
                    <div class="text-xs text-gray-600 flex justify-between">
                      <span><strong>By:</strong> {{ report.value.who }}</span>
                      <span><strong>Duration:</strong> {{ report.value.when?.duration }}ms</span>
                    </div>
                    }
                    <div class="flex gap-2 mt-2">
                      <button (click)="viewS3Report(report)" nz-button nzSize="small"
                        class="bg-white text-primary border-primary hover:bg-primary hover:text-white transition-colors">
                        <i class="fas fa-eye mr-1"></i> View
                      </button>
                      <button (click)="downloadSavedReport(report)" nz-button nzSize="small"
                        class="bg-white text-gray-600 border-gray-300 hover:bg-gray-100 transition-colors">
                        <i class="fas fa-download mr-1"></i> Download
                      </button>
                    </div>
                  </div>
                  }
                </div>
              </div>
            </div>
            }


            @if (activeSidebarTab() === 'Artifacts') {
            <rapider-application-artifacts [appDomain]="currentAppId() || ''"></rapider-application-artifacts>
            }
          </div>
        </div>
      </nz-splitter-panel>
      }
    </nz-splitter>
  </div>

  <ng-template #sidebarTabsTemplate>
    <div class="flex items-center justify-between border-b border-gray-100 px-4 py-3 bg-gray-50/80" sidebar-header>
      <div class="flex items-center space-x-2">
        @if (activeSidebarTab() === 'Tasks') {
        <i class="fa-solid fa-list-check text-primary text-sm"></i>
        <span class="text-sm font-bold text-gray-900">Tasks</span>
        } @else if (activeSidebarTab() === 'E2E Tests') {
        <i class="fa-solid fa-flask text-primary text-sm"></i>
        <span class="text-sm font-bold text-gray-900">E2E Tests</span>
        } @else if (activeSidebarTab() === 'Artifacts') {
        <i class="fa-solid fa-box-archive text-primary text-sm"></i>
        <span class="text-sm font-bold text-gray-900">Artifacts</span>
        } @else {
        <span class="text-sm font-bold text-gray-900">{{ activeSidebarTab() }}</span>
        }
      </div>

      <div class="flex items-center space-x-1.5">
        @if (activeSidebarTab() === 'Tasks') {
        <button type="button" (click)="showGuidanceModal.set(true)"
          class="p-1.5 text-gray-400 hover:text-primary rounded-lg hover:bg-gray-200/60 transition cursor-pointer"
          nzTooltipTitle="Pipeline & Development Guide" nz-tooltip>
          <i class="fa-regular fa-circle-question text-sm"></i>
        </button>
        <button type="button" (click)="showBubbles.set(!showBubbles())"
          class="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-200/60 transition cursor-pointer"
          [nzTooltipTitle]="showBubbles() ? 'Hide Canvas Pins' : 'Show Canvas Pins'" nz-tooltip>
          <i class="fa-regular text-xs" [class.fa-eye]="showBubbles()" [class.fa-eye-slash]="!showBubbles()"></i>
        </button>
        }
        <button type="button" (click)="isSidebarOpen.set(false)"
          class="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-200 focus:outline-none transition cursor-pointer"
          title="Close Sidebar">
          <i class="fa-solid fa-xmark text-sm"></i>
        </button>
      </div>
    </div>
  </ng-template>

  <!-- ======================================================== -->
  <!-- NEW FEATURE REQUEST MODAL                                -->
  <!-- ======================================================== -->
  <nz-modal [nzVisible]="isNewRequestModalVisible()" (nzVisibleChange)="isNewRequestModalVisible.set($event)"
    [nzTitle]="'Submit Feature Request to Developer'" [nzWidth]="'80vw'" nzClassName="rounded-2xl"
    (nzOnCancel)="closeNewRequestModal()" [nzFooter]="newRequestModalFooter">
    <ng-container *nzModalContent>
      <div class="flex flex-col gap-5 py-2">
        <!-- Request Type Selector -->
        <div>
          <label class="block text-xs font-bold text-secondary-text uppercase mb-2">Request Type</label>
          <div class="grid grid-cols-2 @sm:grid-cols-4 gap-2">
            @for (
            t of ['UI Page', 'Data Model', 'New Feature', 'General'];
            track t
            ) {
            <button (click)="onTypeChange($any(t))" type="button"
              class="px-3 py-2 rounded-xl text-xs font-bold border transition-all text-center cursor-pointer" [ngClass]="
                  newRequestType() === t
                    ? 'bg-primary text-white border-primary shadow-sm'
                    : 'bg-alt-background text-secondary-text border-secondary-border hover:border-primary'
                ">
              {{ t }}
            </button>
            }
          </div>
        </div>

        <!-- Target Entity Dropdown (Contextual based on Type) -->
        @if (newRequestType() === 'UI Page') {
        <div>
          <label class="block text-xs font-bold text-secondary-text uppercase mb-1.5">Target UI Page</label>
          <nz-select [ngModel]="newRequestTargetEntity()" (ngModelChange)="newRequestTargetEntity.set($event)"
            class="w-full" nzPlaceHolder="Select an application page...">
            @for (p of availablePages(); track p) {
            <nz-option [nzValue]="p" [nzLabel]="p"></nz-option>
            }
          </nz-select>
        </div>
        } @else if (newRequestType() === 'Data Model') {
        <div>
          <label class="block text-xs font-bold text-secondary-text uppercase mb-1.5">Target Data Model</label>
          <nz-select [ngModel]="newRequestTargetEntity()" (ngModelChange)="newRequestTargetEntity.set($event)"
            class="w-full" nzPlaceHolder="Select a data model...">
            @for (m of availableDataModels(); track m) {
            <nz-option [nzValue]="m" [nzLabel]="m"></nz-option>
            }
          </nz-select>
        </div>
        } @else {
        <div>
          <label class="block text-xs font-bold text-secondary-text uppercase mb-1.5">Component or Feature Area
            (Optional)</label>
          <input nz-input [ngModel]="newRequestTargetEntity()" (ngModelChange)="newRequestTargetEntity.set($event)"
            placeholder="e.g. Export, Navigation, Notifications..." class="rounded-xl" />
        </div>
        }

        <!-- Request Title -->
        <div>
          <label class="block text-xs font-bold text-secondary-text uppercase mb-1.5">Request Title</label>
          <input nz-input [ngModel]="newRequestTitle()" (ngModelChange)="newRequestTitle.set($event)"
            placeholder="Brief summary of your request..." class="rounded-xl text-sm" />
        </div>

        <!-- Description with RapiderMarkdownEditorComponent -->
        <div>
          <label class="block text-xs font-bold text-secondary-text uppercase mb-1.5">
            Detailed Description
            <span class="text-primary font-normal">(Markdown Supported)</span>
          </label>
          <div
            class="border border-secondary-border rounded-xl bg-component-inner min-h-[400px] h-[50vh] flex flex-col overflow-hidden">
            <rapider-markdown-editor class="w-full h-full flex-1 min-h-0 flex flex-col"
              [value]="newRequestDescription()" (valueChange)="newRequestDescription.set($event)"
              placeholder="Provide specifications, expected behavior, screenshots, or mockups..."></rapider-markdown-editor>
          </div>
        </div>
      </div>
    </ng-container>

    <ng-template #newRequestModalFooter>
      <div class="flex items-center justify-end gap-2 pr-2">
        <button nz-button nzType="default" (click)="closeNewRequestModal()" class="rounded-xl font-semibold">
          Cancel
        </button>
        <button nz-button nzType="primary" (click)="submitFeatureRequest()" [nzLoading]="isSubmittingFeatureRequest()"
          class="bg-primary hover:bg-hover-primary text-white border-none rounded-xl font-bold shadow-sm px-5">
          Submit Feature Request
        </button>
      </div>
    </ng-template>
  </nz-modal>

  <!-- E2E Report Preview Modal -->
  <nz-modal [nzVisible]="isReportPreviewVisible()" nzTitle="E2E Test Report Preview"
    (nzOnCancel)="isReportPreviewVisible.set(false)" [nzFooter]="reportPreviewFooter" nzWidth="950px">
    <ng-container *nzModalContent>
      <div
        class="max-h-[75vh] overflow-y-auto p-4 bg-white dark:bg-zinc-900 rounded-lg border border-gray-200 dark:border-zinc-700">
        <rapider-markdown-preview [data]="selectedReportContent()" [showHeader]="false" [showBorder]="false"
          [isTransparent]="true" [disableSanitizer]="true"></rapider-markdown-preview>
      </div>
    </ng-container>
    <ng-template #reportPreviewFooter>
      <div class="flex items-center justify-between w-full">
        <button nz-button nzType="default" (click)="downloadTestReport()"
          class="rounded-lg font-semibold flex items-center gap-1.5" title="Download Report as Markdown">
          <i class="fas fa-download"></i> Download Report
        </button>
        <button nz-button nzType="primary" (click)="isReportPreviewVisible.set(false)"
          class="rounded-lg font-semibold !text-white" style="color: #ffffff !important;">
          Close
        </button>
      </div>
    </ng-template>
  </nz-modal>

  <!-- ======================================================== -->
  <!-- DELETE TASK CONFIRMATION MODAL                           -->
  <!-- ======================================================== -->
  @if (taskToDelete(); as deletingTask) {
  <div class="fixed inset-0 bg-black/40 backdrop-blur-[1px] flex items-center justify-center z-[110]">
    <div
      class="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-sm mx-4 p-5 space-y-4 text-center animate-in fade-in zoom-in-95 duration-150">
      <div class="w-10 h-10 rounded-full bg-rose-100 text-rose-600 mx-auto flex items-center justify-center text-lg">
        <i class="fa-solid fa-triangle-exclamation"></i>
      </div>
      <div>
        <h3 class="font-bold text-sm text-gray-900 m-0">Delete Task</h3>
        <p class="text-xs text-gray-500 mt-1 mb-0">
          Are you sure you want to delete <span class="font-mono font-bold">{{ deletingTask.id }}</span>? This action
          cannot be undone.
        </p>
      </div>
      <div class="flex items-center justify-center space-x-2 pt-1">
        <button type="button" (click)="taskToDelete.set(null)"
          class="px-4 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition cursor-pointer">
          Cancel
        </button>
        <button type="button" (click)="executeDeleteTask()"
          class="px-4 py-1.5 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-lg shadow-sm transition cursor-pointer">
          Delete
        </button>
      </div>
    </div>
  </div>
  }

  <!-- ======================================================== -->
  <!-- CREATE TASK MODAL (BUG REPORT & FEATURE REQUEST)         -->
  <!-- ======================================================== -->
  <nz-modal [nzVisible]="isCreateTaskModalVisible()" [nzTitle]="createTaskModalHeader" [nzWidth]="'900px'"
    nzClassName="rounded-2xl" (nzOnCancel)="closeCreateTaskModal()" [nzFooter]="createTaskModalFooter">
    <ng-template #createTaskModalHeader>
      <div class="flex items-center space-x-2">
        <div class="w-6 h-6 rounded-md bg-primary text-white flex items-center justify-center text-xs">
          <i class="fa-solid fa-plus"></i>
        </div>
        <span class="font-bold text-sm text-gray-900">Create New Task</span>
      </div>
    </ng-template>

    <ng-container *nzModalContent>
      <div class="flex flex-col gap-4 py-1">
        <!-- Title Field -->
        <div>
          <label class="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
            TASK TITLE *
          </label>
          <input nz-input [ngModel]="draftTaskTitle()" (ngModelChange)="draftTaskTitle.set($event)"
            placeholder="e.g., Export button table alignment issue..." class="rounded-lg text-xs" />
        </div>

        <!-- Task Type Selector -->
        <div>
          <label class="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
            TASK TYPE
          </label>
          <div class="grid grid-cols-2 gap-2">
            <button type="button" (click)="draftTaskType.set('Bug')"
              class="px-3 py-2 rounded-lg text-xs font-semibold border transition text-center flex items-center justify-center space-x-1.5 cursor-pointer"
              [ngClass]="draftTaskType() === 'Bug' ? 'bg-rose-50 border-rose-300 text-rose-700 shadow-xs' : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'">
              <i class="fa-solid fa-circle-exclamation text-xs"></i>
              <span>Bug Report</span>
            </button>
            <button type="button" (click)="draftTaskType.set('Feature')"
              class="px-3 py-2 rounded-lg text-xs font-semibold border transition text-center flex items-center justify-center space-x-1.5 cursor-pointer"
              [ngClass]="draftTaskType() === 'Feature' ? 'bg-blue-50 border-blue-300 text-blue-700 shadow-xs' : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'">
              <i class="fa-solid fa-sparkles text-xs"></i>
              <span>Feature Request</span>
            </button>
          </div>
        </div>

        <!-- Micro App DOM Element Pinning -->
        <div>
          <label class="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
            MICRO APP ELEMENT PINNING {{ draftTaskType() === 'Feature' ? '(OPTIONAL)' : '' }}
          </label>
          @if (pinnedMetadata(); as pin) {
          <div class="flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium border"
            [ngClass]="draftTaskType() === 'Bug' ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-blue-50 border-blue-200 text-blue-700'">
            <span class="flex items-center space-x-1.5 truncate mr-2">
              <i class="fa-solid fa-crosshairs"></i>
              <span class="truncate">
                Pinned to &lt;{{ pin.tag || 'element' }}&gt; (X: {{ pin.offsetX }}, Y: {{ pin.offsetY }})
              </span>
            </span>
            <div class="flex items-center space-x-2 shrink-0">
              <button type="button" (click)="startFeatureBubblePin()"
                class="text-[11px] underline hover:opacity-80 cursor-pointer">
                Change Element
              </button>
              <button type="button" (click)="pinnedMetadata.set(null)"
                class="text-gray-400 hover:text-rose-600 p-0.5 cursor-pointer" title="Remove Pin">
                <i class="fa-solid fa-xmark text-xs"></i>
              </button>
            </div>
          </div>
          } @else {
          <button type="button" (click)="startFeatureBubblePin()"
            class="w-full py-2 px-3 border border-dashed rounded-lg text-xs font-semibold flex items-center justify-center space-x-1.5 transition cursor-pointer"
            [ngClass]="draftTaskType() === 'Bug' ? 'border-rose-300 text-rose-600 hover:bg-rose-50' : 'border-blue-300 text-blue-600 hover:bg-blue-50'">
            <i class="fa-solid fa-crosshairs text-xs"></i>
            <span>{{ draftTaskType() === 'Bug' ? 'Drop Bubble on Micro App Element' : 'Drop Bubble on Micro App Element
              (Optional)' }}</span>
          </button>
          }
        </div>

        <!-- Markdown Description Editor with Quick Format Toolbar -->
        <div>
          <div class="flex items-center justify-between mb-1">
            <label class="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
              DESCRIPTION (MARKDOWN & TABLES)
            </label>
          </div>

          <!-- Description Markdown Editor (Rich formatting, tables, and image uploads) -->
          <div
            class="border border-secondary-border rounded-xl bg-component-inner min-h-[350px] flex flex-col overflow-hidden">
            <rapider-markdown-editor class="w-full h-full flex-1 min-h-0 flex flex-col" [value]="draftTaskDescription()"
              (valueChange)="draftTaskDescription.set($event)"
              placeholder="Describe the task details. You can insert tables, format with markdown, or drag and drop images..."
              [isPublic]="true" fileType="task"
              [parentEntityId]="currentAppId() || undefined"></rapider-markdown-editor>
          </div>
        </div>
      </div>
    </ng-container>

    <ng-template #createTaskModalFooter>
      <div class="flex items-center justify-end space-x-2">
        <button nz-button nzType="default" (click)="closeCreateTaskModal()"
          class="rounded-lg text-xs font-semibold cursor-pointer">
          Cancel
        </button>
        <button nz-button nzType="primary" (click)="handleCreateTaskSubmit()" [nzLoading]="isSubmittingTask()"
          [disabled]="!draftTaskTitle().trim()"
          class="bg-primary hover:bg-hover-primary text-white border-none rounded-lg text-xs font-bold shadow-sm px-4 cursor-pointer">
          Create Task
        </button>
      </div>
    </ng-template>
  </nz-modal>

  <!-- ======================================================== -->
  <!-- REJECT SOLUTION & REQUEST CHANGES MODAL                  -->
  <!-- ======================================================== -->
  <nz-modal [nzVisible]="isRejectModalVisible()" [nzTitle]="rejectModalHeader" [nzWidth]="'560px'"
    nzClassName="rounded-2xl" (nzOnCancel)="closeRejectModal()" [nzFooter]="rejectModalFooter">
    <ng-template #rejectModalHeader>
      <div class="flex items-center space-x-2">
        <div class="w-6 h-6 rounded-md bg-rose-600 text-white flex items-center justify-center text-xs">
          <i class="fa-solid fa-rotate-left"></i>
        </div>
        <span class="font-bold text-sm text-gray-900">
          {{ getNormalizedTaskStatus(taskPendingRejection()) === 'Released' ? 'Reopen Task' : 'Reject Solution & Request
          Changes' }}
        </span>
      </div>
    </ng-template>

    <ng-container *nzModalContent>
      <div class="flex flex-col gap-3 py-1">
        <div class="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-800 leading-relaxed">
          <p class="font-semibold mb-1 flex items-center gap-1.5">
            <i class="fa-solid fa-circle-exclamation text-rose-600"></i>
            <span>
              {{ getNormalizedTaskStatus(taskPendingRejection()) === 'Released' ? 'Why are you reopening this task?' :
              'Why is this solution not resolved?' }}
            </span>
          </p>
          <span>
            Please describe why the issue is not resolved, including any remaining bugs or missing behavior.
            Your explanation will be recorded as an official reply in the task thread so the AI agent and team know what
            needs to be fixed.
          </span>
        </div>

        <div>
          <label class="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">
            {{ getNormalizedTaskStatus(taskPendingRejection()) === 'Released' ? 'REOPEN REASON / EXPLANATION *' :
            'REJECTION REASON / EXPLANATION *' }}
          </label>
          <textarea nz-input [rows]="4" [ngModel]="rejectionReason()" (ngModelChange)="rejectionReason.set($event)"
            placeholder="e.g., The table height does not fit 100% in desktop view and collapses when switching from mobile back to tablet..."
            class="rounded-lg text-xs w-full"></textarea>
        </div>

        <p class="text-[11px] text-gray-500 italic m-0">
          @if (getNormalizedTaskStatus(taskPendingRejection()) === 'Released') {
          Note: Reopening this task will change its status to <strong>Reopened</strong> and require Owner/Admin approval
          before re-execution.
          } @else {
          Note: Rejecting the solution will reset the task status to <strong>Open</strong> and require Owner/Admin
          approval before re-execution.
          }
        </p>
      </div>
    </ng-container>

    <ng-template #rejectModalFooter>
      <div class="flex items-center justify-end space-x-2">
        <button nz-button nzType="default" (click)="closeRejectModal()" class="rounded-lg text-xs">
          Cancel
        </button>
        <button nz-button nzType="primary" [nzLoading]="isSubmittingRejection()"
          [disabled]="!rejectionReason().trim() || isSubmittingRejection()" (click)="confirmRejectSolution()"
          class="bg-rose-600 hover:bg-rose-700 !text-white text-white border-none rounded-lg text-xs font-bold shadow-sm px-4 cursor-pointer"
          style="color: #ffffff !important;">
          <i class="fa-solid fa-rotate-left mr-1"></i>
          <span>{{ getNormalizedTaskStatus(taskPendingRejection()) === 'Released' ? 'Reopen Task' : 'Submit Rejection'
            }}</span>
        </button>
      </div>
    </ng-template>
  </nz-modal>

  <!-- ======================================================== -->
  <!-- EDIT TASK APPROVAL STATUS MODAL                          -->
  <!-- ======================================================== -->
  <nz-modal [nzVisible]="isEditApprovalModalVisible()" [nzTitle]="editApprovalModalHeader" [nzWidth]="'480px'"
    nzClassName="rounded-2xl" (nzOnCancel)="closeEditApprovalModal()" [nzFooter]="editApprovalModalFooter">
    <ng-template #editApprovalModalHeader>
      <div class="flex items-center space-x-2">
        <div class="w-6 h-6 rounded-md bg-primary text-white flex items-center justify-center text-xs">
          <i class="fa-solid fa-pencil"></i>
        </div>
        <span class="font-bold text-sm text-gray-900">Edit Task Approval Status</span>
      </div>
    </ng-template>

    <ng-container *nzModalContent>
      <div class="flex flex-col gap-3 py-1">
        <p class="text-xs text-gray-600 m-0">
          Switch the approval status for <strong>{{ editingApprovalTask()?.title }}</strong>.
        </p>

        <div class="grid grid-cols-2 gap-3 pt-2">
          <!-- Option: Approved -->
          <label class="flex items-center gap-2.5 p-3 rounded-xl border cursor-pointer transition-all" [ngClass]="editingApprovalValue() === 'approved'
              ? 'bg-emerald-50 border-emerald-300 ring-2 ring-emerald-200'
              : 'bg-white border-gray-200 hover:border-gray-300'">
            <input type="radio" name="approvalValue" [value]="'approved'"
              [checked]="editingApprovalValue() === 'approved'" (change)="editingApprovalValue.set('approved')"
              class="text-emerald-600 focus:ring-emerald-500" />
            <div class="flex flex-col">
              <span class="text-xs font-bold text-emerald-900 flex items-center gap-1">
                <i class="fa-solid fa-check text-emerald-600 text-xs"></i> Approved
              </span>
              <span class="text-[10px] text-gray-500">Allow AI execution</span>
            </div>
          </label>

          <!-- Option: Rejected -->
          <label class="flex items-center gap-2.5 p-3 rounded-xl border cursor-pointer transition-all" [ngClass]="editingApprovalValue() === 'rejected'
              ? 'bg-rose-50 border-rose-300 ring-2 ring-rose-200'
              : 'bg-white border-gray-200 hover:border-gray-300'">
            <input type="radio" name="approvalValue" [value]="'rejected'"
              [checked]="editingApprovalValue() === 'rejected'" (change)="editingApprovalValue.set('rejected')"
              class="text-rose-600 focus:ring-rose-500" />
            <div class="flex flex-col">
              <span class="text-xs font-bold text-rose-900 flex items-center gap-1">
                <i class="fa-solid fa-xmark text-rose-600 text-xs"></i> Rejected
              </span>
              <span class="text-[10px] text-gray-500">Decline this task</span>
            </div>
          </label>
        </div>
      </div>
    </ng-container>

    <ng-template #editApprovalModalFooter>
      <div class="flex items-center justify-end space-x-2">
        <button nz-button nzType="default" (click)="closeEditApprovalModal()" class="rounded-lg text-xs">
          Cancel
        </button>
        <button nz-button nzType="primary" (click)="confirmEditApproval()"
          class="bg-primary hover:opacity-90 text-white border-none rounded-lg text-xs font-bold shadow-sm px-4 cursor-pointer"
          style="color: #ffffff !important;">
          <span>Save Changes</span>
        </button>
      </div>
    </ng-template>
  </nz-modal>

  <!-- PIPELINE & DEVELOPMENT GUIDANCE MODAL -->
  <rapider-task-pipeline-guide-modal [(isVisible)]="showGuidanceModal"></rapider-task-pipeline-guide-modal>
</div>